import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../utils/api';
import { LogoLockup } from '../components/Icons';
import ClaveInput from '../components/ClaveInput';
import * as IA from '../utils/asistente';
import { leerArrastre, leerSeleccion, esBasura, type ArchivoConRuta } from '../utils/importarCarpetas';
import ModuloForo from './consola/ModuloForo';
import Visitas from './consola/Visitas';

// Consola privada del dueño de Tabularis: listado de todos los estudios con su
// contacto y actividad (cobro y estado). Login separado de los estudios.
const C = { navy: '#0D1B35', blue: '#1A3A6E', blue2: '#2756A8', line: '#DDE6F3', muted: '#6B82A0', ok: '#1F7A52' };
const TK = 'plataforma_token';

type Cobro = { ciclo: string; meses: number; totalClp: number; ahorroClp: number; mensualEquivalenteClp: number; panelClp: number };
type Estudio = { id: string; nombre: string; subdominio: string; estado: string; contactoNombre: string | null; contactoEmail: string | null; usuarios: number; casos: number; creado: string;
  plan?: string | null;
  planId?: string | null;
  periodoFin?: string | null;
  diasRestantes?: number | null;
  /** Baja en curso: fecha desde la que se puede borrar definitivamente. */
  bajaProgramada?: string | null;
  diasParaBorrar?: number | null;
  /** Abogados activos: es lo que se cobra. */
  abogados?: number;
  porCupos?: boolean;
  cupos?: number | null;
  excedeCupos?: boolean;
  ciclo?: string;
  cobro?: Cobro | null;
  cuposIlimitados?: boolean;
  precioAcordado?: boolean;
  precioClp?: number | null;
  /** Quién lo trajo, si llegó con código de referido. */
  aliado?: { id: string; codigo: string; nombre: string } | null;
  /** Tiene el panel; falso = solo Chatbularis (que todos tienen, prepago). */
  conPanel?: boolean;
  /** Módulos en prueba prendidos para este estudio (hoy: "contabilidad"). */
  modulos?: string[];
  /** Saldo prepago de Chatbularis. Null = nunca lo abrió; bajo el mínimo = lo
   *  usa y está detenido hasta que cargue. Son cosas distintas. */
  saldoIaClp?: number | null;
  /** Sin tope: no se detiene por saldo. Un negativo acá es gasto, no deuda
   *  que cobrar. */
  saldoIaSinTope?: boolean;
  /** Días para recuperar la información tras el término. Null = el estándar. */
  graciaDias?: number | null;
  graciaDiasDefecto?: number;
  /** Ventana de rescate abierta a mano: hasta cuándo puede entrar a descargar. */
  reaperturaHasta?: string | null;
  mesesReapertura?: number;
  /** Último día en que se le puede reabrir el acceso (término + 6 meses). */
  rescateHasta?: string | null;
  /** Pasaron los 6 meses: su información ya no se le puede entregar y toca
   *  borrarla. No se borra sola — el borrado sigue siendo el flujo de Baja. */
  paraBorrar?: boolean;
  /** Cuándo se le mandó el correo de bienvenida desde la consola. */
  bienvenidaEnviada?: string | null;
};
type Plan = { id: string; nombre: string; precioClp: number; porCupos: boolean; maxUsuarios: number; cuposIlimitados?: boolean; precioAcordado?: boolean };
/** Saldo mínimo para empezar una consulta de Chatbularis. Espejo de
 *  MINIMO_PARA_EMPEZAR_CLP del backend (Redactar y Conversar). */
const MINIMO_IA_CLP = 1000;
const fmt = (s: string) => { try { return new Date(s).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return ''; } };
// Día chileno de una fecha ISO, como lo quiere un <input type="date">. El
// vencimiento se guarda al final del día, que en UTC ya es el día siguiente:
// cortar el ISO a diez caracteres mostraría una fecha corrida en un día.
// Una fecha suelta ("2026-09-30") se arma a mano: pasarla por Date la lee en
// UTC y en Chile se muestra el día anterior.
const fmtDia = (d: string) => { const [y, m, dd] = d.split('-'); return `${dd}/${m}/${y}`; };
const diaChile = (iso?: string | null) => {
  if (!iso) return '';
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' }).format(new Date(iso)); } catch { return ''; }
};
const clp = (n: number | null | undefined) => (typeof n === 'number' ? '$' + n.toLocaleString('es-CL') : '—');
const CICLOS: Record<string, string> = { mensual: 'Mensual', semestral: 'Semestral', anual: 'Anual' };

// ── Estado de un estudio en una sola palabra ─────────────────────────────
//
// La tabla vieja repartía las alertas en cinco columnas ("faltan cupos" en
// abogados, "agotada" en plan, "PLAZO CUMPLIDO" en vence) y había que leer
// la fila entera para saber qué hacer. Acá cada estudio tiene UN estado, y el
// rojo queda solo para lo que exige una acción tuya.
type Tono = 'ok' | 'warn' | 'bad' | 'info' | 'gris' | 'cortesia';
const TONOS: Record<Tono, [string, string]> = {
  ok: ['#E6F4EC', '#1F7A52'], warn: ['#FFF4DC', '#8A5A00'], bad: ['#FBEAEA', '#A32A2A'],
  info: ['#EEF4FD', '#2756A8'], gris: ['#EEF2F8', '#6B82A0'],
  // Verde azulado: no es azul como la prueba ni verde como "Al día" (que paga).
  cortesia: ['#E0F4F1', '#0F766E'],
};
const deBaja = (e: Estudio) => e.estado === 'CANCELADO' || !!e.bajaProgramada;
const listoParaBorrar = (e: Estudio) => !!e.paraBorrar || (!!e.bajaProgramada && (e.diasParaBorrar ?? 1) <= 0);
/** Paga algo: hay un cobro mayor a cero (solo el panel: Chatbularis es
 *  prepago y no entra en la suscripción). */
const cobrable = (e: Estudio) => !!e.cobro && e.cobro.totalClp > 0;
const porCobrar = (e: Estudio) =>
  !deBaja(e) && e.estado !== 'TRIAL' && cobrable(e) && e.diasRestantes != null && e.diasRestantes <= 7;
const iaDetenido = (e: Estudio) => !deBaja(e) && e.saldoIaClp != null && !e.saldoIaSinTope && e.saldoIaClp < MINIMO_IA_CLP;
const excede = (e: Estudio) => !deBaja(e) && !!e.excedeCupos && !e.cuposIlimitados;

function estadoDe(e: Estudio): { txt: string; tono: Tono } {
  if (listoParaBorrar(e)) return { txt: 'Borrar datos', tono: 'bad' };
  if (deBaja(e)) return { txt: 'De baja', tono: 'gris' };
  if (e.estado === 'SUSPENDIDO') return { txt: 'Suspendido', tono: 'bad' };
  // Solo Chatbularis no tiene prueba ni vencimiento: es prepago.
  if (e.conPanel === false) return { txt: 'Solo Chatbularis', tono: 'info' };
  // Una prueba que ya terminó se dice: el estudio quedó sin acceso igual que
  // uno que dejó de pagar, y mostrarla como "En prueba" lo escondía.
  if (e.estado === 'TRIAL') {
    return e.diasRestantes != null && e.diasRestantes < 0
      ? { txt: 'Prueba vencida', tono: 'bad' }
      : { txt: 'En prueba', tono: 'info' };
  }
  if (!cobrable(e)) return { txt: 'Cortesía', tono: 'cortesia' };
  const d = e.diasRestantes;
  if (d == null) return { txt: 'Sin término', tono: 'ok' };
  if (d < 0) return { txt: 'Vencido', tono: 'bad' };
  if (d <= 7) return { txt: 'Por vencer', tono: 'warn' };
  return { txt: 'Al día', tono: 'ok' };
}
const Estado: React.FC<{ e: Estudio; chico?: boolean }> = ({ e, chico }) => {
  const { txt, tono } = estadoDe(e);
  const [bg, fg] = TONOS[tono];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: bg, color: fg, borderRadius: 999, padding: chico ? '1px 8px' : '3px 10px', fontSize: chico ? 11.5 : 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: fg }} />{txt}
    </span>
  );
};
const venceTxt = (d?: number | null) => d == null ? 'sin término' : d < 0 ? `hace ${-d} ${d === -1 ? 'día' : 'días'}` : d === 0 ? 'hoy' : `en ${d} ${d === 1 ? 'día' : 'días'}`;

/** El saldo prepago de Chatbularis. Sin tope se muestra lo gastado. */
const CuotaIA: React.FC<{ e: Estudio }> = ({ e }) => {
  if (e.saldoIaClp == null) return <span style={{ fontSize: 12, color: C.muted }}>Sin abrir</span>;
  if (e.saldoIaSinTope) {
    return <div style={{ fontSize: 12, color: C.muted }}>Sin tope<div style={{ fontVariantNumeric: 'tabular-nums' }}>{e.saldoIaClp < 0 ? `${clp(-e.saldoIaClp)} gastado` : `${clp(e.saldoIaClp)} saldo`}</div></div>;
  }
  const detenido = e.saldoIaClp < MINIMO_IA_CLP;
  const poco = e.saldoIaClp < 5000;
  return (
    <div style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', fontWeight: detenido ? 600 : 400, color: detenido ? '#A32A2A' : poco ? '#9A6A12' : C.navy }}>
      {clp(e.saldoIaClp)}
      <div style={{ fontSize: 11.5, color: detenido ? '#A32A2A' : C.muted, fontWeight: 400 }}>{detenido ? 'sin saldo' : 'de saldo'}</div>
    </div>
  );
};

type Seccion = 'hoy' | 'estudios' | 'chatbularis' | 'aliados' | 'foro' | 'visitas';
const SECCIONES: [Seccion, string][] = [['hoy', 'Hoy'], ['estudios', 'Estudios'], ['chatbularis', 'Chatbularis'], ['aliados', 'Aliados'], ['foro', 'Foro'], ['visitas', 'Visitas']];
type Filtro = 'todos' | 'cobrar' | 'prueba' | 'cortesia' | 'solo-ia' | 'baja';
const FILTROS: [Filtro, string, (e: Estudio) => boolean][] = [
  ['todos', 'Todos', (e) => !deBaja(e)],
  ['cobrar', 'Por cobrar', porCobrar],
  ['prueba', 'En prueba', (e) => !deBaja(e) && e.estado === 'TRIAL'],
  ['cortesia', 'Cortesía', (e) => !deBaja(e) && e.estado !== 'TRIAL' && e.conPanel !== false && !cobrable(e)],
  ['solo-ia', 'Solo Chatbularis', (e) => !deBaja(e) && e.conPanel === false],
  ['baja', 'De baja', deBaja],
];

const ANGOSTO = '(max-width: 720px)';

const PlataformaConsola: React.FC = () => {
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem(TK));
  const [estudios, setEstudios] = useState<Estudio[] | null>(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  // La sección va en la dirección (#estudios) para que recargar no te
  // devuelva siempre a la portada.
  const [seccion, setSeccionState] = useState<Seccion>(() => {
    const h = window.location.hash.slice(1) as Seccion;
    return SECCIONES.some(([id]) => id === h) ? h : 'hoy';
  });
  const setSeccion = (s: Seccion) => { setSeccionState(s); window.history.replaceState(null, '', `#${s}`); window.scrollTo(0, 0); };
  // La ficha se guarda por id y no por objeto: así, cuando la lista se
  // recarga después de un cambio, la ficha muestra el estudio actualizado.
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [fichaPestana, setFichaPestana] = useState<PestanaFicha>('resumen');
  const [verBaja, setVerBaja] = useState<Estudio | null>(null);
  const [verClave, setVerClave] = useState(false);
  const [verInvitar, setVerInvitar] = useState(false);
  const [denunciasPendientes, setDenunciasPendientes] = useState(0);
  const [usoIa, setUsoIa] = useState<UsoIA | null>(null);
  const [comisionesPendientes, setComisionesPendientes] = useState<number | null>(null);
  // En el teléfono (la consola instalada como app) la tabla de estudios se
  // vuelve tarjetas: con siete columnas había que desplazarse de lado.
  const [angosto, setAngosto] = useState(() => window.matchMedia(ANGOSTO).matches);
  useEffect(() => {
    const mq = window.matchMedia(ANGOSTO);
    const cambio = () => setAngosto(mq.matches);
    mq.addEventListener('change', cambio);
    return () => mq.removeEventListener('change', cambio);
  }, []);

  const cargar = useCallback(async (t: string) => {
    try {
      setEstudios(await api<Estudio[]>('/plataforma/estudios', { token: t }));
    } catch (e: any) {
      if (e?.status === 401 || e?.status === 403) { localStorage.removeItem(TK); setTokenState(null); }
      else setError('No se pudo cargar. Intenta de nuevo.');
    }
  }, []);

  useEffect(() => { if (token) cargar(token); }, [token, cargar]);

  const apiConsola = useCallback(
    <T,>(ruta: string, opciones: any = {}) => api<T>(ruta, { ...opciones, token }),
    [token],
  );

  // Lo que necesita "Hoy" además de la lista: denuncias del foro, crédito de
  // Anthropic y comisiones por pagar. Se refresca al cambiar de sección, no
  // con un sondeo: nada de esto es urgente de minutos.
  useEffect(() => {
    if (!token) return;
    apiConsola<{ pendientes: number }>('/plataforma/foro/pendientes').then((r) => setDenunciasPendientes(r.pendientes)).catch(() => {});
    if (seccion !== 'hoy') return;
    apiConsola<UsoIA>('/plataforma/ia').then(setUsoIa).catch(() => {});
    apiConsola<Aliado[]>('/plataforma/aliados').then((l) => setComisionesPendientes(l.reduce((a, x) => a + x.pendienteClp, 0))).catch(() => {});
  }, [token, apiConsola, seccion]);

  if (!token) return <Login onLogin={(t) => { localStorage.setItem(TK, t); setTokenState(t); }} />;

  const lista = estudios || [];
  const abrir = (e: Estudio, pestana: PestanaFicha = 'resumen') => { setFichaPestana(pestana); setFichaId(e.id); };
  const ficha = fichaId ? lista.find((e) => e.id === fichaId) ?? null : null;
  const salir = () => { localStorage.removeItem(TK); setTokenState(null); setEstudios(null); };
  const pendientesHoy = lista.filter((e) => porCobrar(e) || iaDetenido(e) || excede(e) || listoParaBorrar(e)).length;

  const fn = FILTROS.find(([id]) => id === filtro)![2];
  const filtrados = lista.filter(fn).filter((e) =>
    `${e.nombre} ${e.subdominio} ${e.contactoNombre || ''} ${e.contactoEmail || ''}`.toLowerCase().includes(q.toLowerCase()));

  const linkBtn: React.CSSProperties = { background: 'none', border: `1px solid ${C.line}`, borderRadius: 8, padding: '6px 12px', color: C.muted, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' };
  return (
    <div style={{ fontFamily: 'Inter, sans-serif', minHeight: '100vh', background: '#F5F8FC', color: C.navy }}>
      <header style={{ background: '#fff', borderBottom: `1px solid ${C.line}`, position: 'sticky', top: 0, zIndex: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px clamp(16px, 3vw, 40px) 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.blue }}>
            <LogoLockup height={26} />
            <span style={{ fontSize: 12, color: C.muted, border: `1px solid ${C.line}`, borderRadius: 6, padding: '3px 8px', marginLeft: 6, whiteSpace: 'nowrap' }}>Consola</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setVerClave(true)} style={linkBtn}>Contraseña</button>
            <button onClick={salir} style={linkBtn}>Salir</button>
          </div>
        </div>
        <nav aria-label="Secciones" style={{ display: 'flex', gap: 2, padding: '0 clamp(16px, 3vw, 40px)', overflowX: 'auto' }}>
          {SECCIONES.map(([id, etiqueta]) => {
            const cuenta = id === 'hoy' ? pendientesHoy : id === 'foro' ? denunciasPendientes : 0;
            return (
              <button key={id} onClick={() => setSeccion(id)} aria-current={seccion === id ? 'page' : undefined}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '12px 14px 11px', whiteSpace: 'nowrap',
                  color: seccion === id ? C.blue : C.muted, fontWeight: seccion === id ? 600 : 400,
                  borderBottom: `2px solid ${seccion === id ? C.blue : 'transparent'}`, display: 'flex', alignItems: 'center', gap: 7,
                }}>
                {etiqueta}
                {cuenta > 0 && <span style={{ background: '#A32A2A', color: '#fff', borderRadius: 999, fontSize: 11, padding: '1px 7px', fontWeight: 600 }}>{cuenta}</span>}
              </button>
            );
          })}
        </nav>
      </header>

      <main style={{ maxWidth: 1320, margin: '0 auto', padding: '26px clamp(16px, 3vw, 40px) 60px' }}>
        {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 14 }}>{error}</div>}

        {seccion === 'hoy' && (
          <Hoy estudios={estudios} usoIa={usoIa} denuncias={denunciasPendientes} comisiones={comisionesPendientes}
               onAbrir={abrir} onSeccion={setSeccion} />
        )}

        {seccion === 'estudios' && (<>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={titulo}>Estudios</h1>
              <p style={{ color: C.muted, margin: '4px 0 0', fontSize: 14 }}>
                {estudios ? `${lista.filter((e) => !deBaja(e)).length} activos. ${angosto ? "Toca uno" : "Haz clic en una fila"} para ver y cambiar todo lo del estudio.` : 'Cargando…'}
              </p>
            </div>
            <button onClick={() => setVerInvitar(true)} style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>＋ Invitar</button>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 18 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
              {FILTROS.map(([id, etiqueta, f]) => {
                const activo = filtro === id;
                return (
                  <button key={id} onClick={() => setFiltro(id)} aria-pressed={activo}
                    style={{ border: `1px solid ${activo ? C.navy : C.line}`, background: activo ? C.navy : '#fff', color: activo ? '#fff' : C.navy, borderRadius: 999, padding: '6px 12px', fontSize: 13, cursor: 'pointer', display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                    {etiqueta} <b style={{ fontWeight: 600, color: activo ? '#BFD3F5' : C.muted }}>{lista.filter(f).length}</b>
                  </button>
                );
              })}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar estudio o correo…"
              style={{ padding: '8px 12px', border: `1px solid ${C.line}`, borderRadius: 9, fontSize: 14, width: 'min(240px, 100%)' }} />
          </div>

          {angosto ? (
            <div style={{ marginTop: 14, border: `1px solid ${C.line}`, borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
              {filtrados.map((e, i) => (
                <div key={e.id} onClick={() => abrir(e)} style={{ padding: '13px 14px', borderTop: i ? `1px solid ${C.line}` : 'none', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{e.nombre}</div>
                      <div style={{ fontSize: 12, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.subdominio}.tabularis.cl</div>
                    </div>
                    <Estado e={e} chico />
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8, fontSize: 12.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
                    <span>{cobrable(e) ? <><b style={{ color: C.navy, fontWeight: 600 }}>{clp(e.cobro!.totalClp)}</b> {CICLOS[e.ciclo || 'mensual'].toLowerCase()}</> : 'Sin cobro'}</span>
                    <span>{e.periodoFin ? <>vence {venceTxt(e.diasRestantes)}</> : 'sin término'}</span>
                    <span>IA: {e.saldoIaClp == null ? 'sin abrir' : e.saldoIaSinTope ? 'sin tope' : clp(e.saldoIaClp)}</span>
                  </div>
                  {cobrable(e) && !deBaja(e) && (
                    <div style={{ marginTop: 10 }} onClick={(ev) => ev.stopPropagation()}>
                      <BotonPago estudio={e} token={token} onCambio={() => cargar(token)} onError={setError} />
                    </div>
                  )}
                </div>
              ))}
              {estudios && filtrados.length === 0 && (
                <div style={{ textAlign: 'center', color: C.muted, padding: 28, fontSize: 14 }}>Sin estudios en este filtro.</div>
              )}
            </div>
          ) : (
          <div style={{ overflowX: 'auto', marginTop: 14, border: `1px solid ${C.line}`, borderRadius: 12, background: '#fff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 920, fontSize: 14 }}>
              <thead>
                <tr style={{ background: '#F7FAFD', color: C.muted, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  <Th>Estudio</Th><Th>Contrató</Th><Th>Cobro</Th><Th>Vence</Th><Th>Chatbularis</Th><Th>Estado</Th><Th>{''}</Th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((e) => (
                  <tr key={e.id} onClick={() => abrir(e)} style={{ borderTop: `1px solid ${C.line}`, cursor: 'pointer' }}
                      onMouseEnter={(ev) => (ev.currentTarget.style.background = '#FAFCFF')} onMouseLeave={(ev) => (ev.currentTarget.style.background = '')}>
                    <td style={{ ...td, minWidth: 180 }}>
                      <div style={{ fontWeight: 600 }}>{e.nombre}</div>
                      <div style={{ fontSize: 12, color: C.muted }}>{e.subdominio}.tabularis.cl{e.aliado ? ` · vía ${e.aliado.codigo}` : ''}</div>
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {e.conPanel !== false && <span style={chip}>Panel · {e.abogados ?? 0}{e.cuposIlimitados ? '' : e.porCupos ? `/${e.cupos ?? 0}` : ''} abog.</span>}
                        {(e.conPanel === false || e.saldoIaSinTope) && (
                          <span style={{ ...chip, background: '#E9E6FB', color: '#4A3A9C' }}>
                            {e.saldoIaSinTope ? 'IA sin tope' : 'Solo IA'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {cobrable(e) ? (
                        <>
                          <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{clp(e.cobro!.totalClp)}</div>
                          <div style={{ fontSize: 12, color: C.muted }}>{CICLOS[e.ciclo || 'mensual']}{e.precioAcordado ? ' · acordado' : ''}</div>
                        </>
                      ) : <span style={{ fontSize: 12.5, color: C.muted }}>Sin cobro</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {e.periodoFin ? <>{venceTxt(e.diasRestantes)}<div style={{ fontSize: 12, color: C.muted }}>{fmt(e.periodoFin)}</div></> : <span style={{ color: C.muted }}>sin término</span>}
                    </td>
                    <td style={td}><CuotaIA e={e} /></td>
                    <td style={td}><Estado e={e} /></td>
                    <td style={{ ...td, textAlign: 'right' }} onClick={(ev) => ev.stopPropagation()}>
                      {cobrable(e) && !deBaja(e) && <BotonPago estudio={e} token={token} onCambio={() => cargar(token)} onError={setError} />}
                    </td>
                  </tr>
                ))}
                {estudios && filtrados.length === 0 && (
                  <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: C.muted, padding: 28 }}>Sin estudios en este filtro.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </>)}

        {seccion === 'chatbularis' && (<>
          <h1 style={titulo}>Chatbularis</h1>
          <p style={{ color: C.muted, margin: '4px 0 0', fontSize: 14 }}>Lo global del asistente. La cuota de cada estudio está en su ficha.</p>
          <PanelIA token={token} />
          <BaseJuridica token={token} />
        </>)}

        {seccion === 'aliados' && <PanelAliados token={token} />}

        {seccion === 'foro' && <ModuloForo api={apiConsola} />}
        {seccion === 'visitas' && <Visitas api={apiConsola} />}
      </main>

      {ficha && (
        <FichaEstudio key={ficha.id} estudio={ficha} token={token} pestanaInicial={fichaPestana}
          onCerrar={() => setFichaId(null)} onCambio={() => cargar(token)} onBaja={() => setVerBaja(ficha)} />
      )}
      {verBaja && <PanelBaja estudio={verBaja} token={token} onCerrar={() => setVerBaja(null)} onCambio={() => cargar(token)} />}
      {verClave && <CambiarClave token={token} onCerrar={() => setVerClave(false)} />}
      {verInvitar && <PanelInvitar onCerrar={() => setVerInvitar(false)} />}
    </div>
  );
};

const titulo: React.CSSProperties = { fontFamily: 'Playfair Display, serif', fontSize: 26, margin: 0 };
const chip: React.CSSProperties = { fontSize: 11.5, borderRadius: 6, padding: '2px 7px', background: '#EEF2F8', color: C.blue, fontWeight: 500, whiteSpace: 'nowrap' };

// ── Hoy ──────────────────────────────────────────────────────────────────
//
// La portada: lo que pide una acción tuya, contado y con nombre. Es lo que
// antes había que sacar leyendo la tabla fila por fila. Cada estudio abre su
// ficha en la pestaña donde se resuelve.
const Hoy: React.FC<{
  estudios: Estudio[] | null; usoIa: UsoIA | null; denuncias: number; comisiones: number | null;
  onAbrir: (e: Estudio, p?: PestanaFicha) => void; onSeccion: (s: Seccion) => void;
}> = ({ estudios, usoIa, denuncias, comisiones, onAbrir, onSeccion }) => {
  if (!estudios) return <div style={{ color: C.muted }}>Cargando…</div>;
  const activos = estudios.filter((e) => !deBaja(e));
  const cobrar = estudios.filter(porCobrar).sort((a, b) => (a.diasRestantes ?? 0) - (b.diasRestantes ?? 0));
  const detenidos = estudios.filter(iaDetenido);
  const cupos = estudios.filter(excede);
  const pruebas = activos.filter((e) => e.estado === 'TRIAL').sort((a, b) => (a.diasRestantes ?? 99) - (b.diasRestantes ?? 99));
  const borrar = estudios.filter(listoParaBorrar);
  const mensual = activos.filter((e) => e.estado === 'ACTIVO' && cobrable(e)).reduce((a, e) => a + (e.cobro?.mensualEquivalenteClp ?? 0), 0);
  const creditoBajo = usoIa?.diasRestantes != null && usoIa.diasRestantes < 21;

  const Tarjeta: React.FC<{ titulo: string; tono: 'bad' | 'warn'; items: [Estudio, string][]; vacio: string; pie?: string; pestana?: PestanaFicha }> =
    ({ titulo: t, tono, items, vacio, pie, pestana }) => (
      <section style={{ background: '#fff', border: `1px solid ${C.line}`, borderLeft: items.length ? `4px solid ${tono === 'bad' ? '#A32A2A' : '#D9A23A'}` : `1px solid ${C.line}`, borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14.5 }}>{t}</span>
          <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: items.length ? C.navy : C.muted }}>{items.length}</span>
        </div>
        {items.length ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {items.map(([e, extra]) => (
              <button key={e.id} onClick={() => onAbrir(e, pestana)} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', padding: '6px 8px', margin: '0 -8px', borderRadius: 7, background: 'none', border: 'none', textAlign: 'left', fontSize: 13.5, color: C.navy, cursor: 'pointer' }}
                onMouseEnter={(ev) => (ev.currentTarget.style.background = '#EEF4FD')} onMouseLeave={(ev) => (ev.currentTarget.style.background = 'none')}>
                <span>{e.nombre}</span><span style={{ fontSize: 12, color: C.muted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{extra}</span>
              </button>
            ))}
          </div>
        ) : <div style={{ fontSize: 13, color: C.ok }}>✓ {vacio}</div>}
        {pie && <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{pie}</div>}
      </section>
    );

  const cifra = (k: string, v: string, p: string, alerta?: boolean) => (
    <div style={{ padding: '14px 18px', borderRight: `1px solid ${C.line}` }}>
      <div style={{ fontSize: 12, color: C.muted }}>{k}</div>
      <div style={{ fontSize: 21, fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginTop: 2, color: alerta ? '#8A5A00' : C.navy }}>{v}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 1 }}>{p}</div>
    </div>
  );

  return (<>
    <h1 style={titulo}>Hoy</h1>
    <p style={{ color: C.muted, margin: '4px 0 0', fontSize: 14 }}>Lo que necesita tu atención. Cada estudio abre su ficha.</p>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14, marginTop: 20 }}>
      <Tarjeta titulo="Por cobrar" tono="bad" vacio="Nadie vence esta semana"
        items={cobrar.map((e) => [e, `${clp(e.cobro!.totalClp)} · ${(e.diasRestantes ?? 0) < 0 ? 'venció ' : 'vence '}${venceTxt(e.diasRestantes)}`])}
        pie="Vencidos y los que vencen en 7 días o menos. El pago se registra desde la ficha." />
      <Tarjeta titulo="Chatbularis detenido" tono="bad" vacio="Todos pueden consultar" pestana="ia"
        items={detenidos.map((e) => [e, `saldo ${clp(e.saldoIaClp ?? 0)}`])}
        pie="Sin saldo para una consulta nueva. Cargan solos por Banchile Pagos; desde la ficha puedes regalarle saldo." />
      <Tarjeta titulo="Más abogados que cupos" tono="warn" vacio="Todos dentro de sus cupos" pestana="plan"
        items={cupos.map((e) => [e, `${e.abogados} de ${e.cupos} cupos`])}
        pie="No se les corta el acceso; conviene llamarlos." />
      <Tarjeta titulo="Pruebas en curso" tono="warn" vacio="Sin pruebas abiertas"
        items={pruebas.map((e) => [e, `${e.diasRestantes != null ? `termina ${venceTxt(e.diasRestantes)}` : 'sin término'}${e.bienvenidaEnviada ? '' : ' · sin bienvenida'}`])}
        pie="Buen momento para escribirles: en la ficha está el correo de bienvenida, con el video de Importar." />
      <Tarjeta titulo="Borrar datos" tono="bad" vacio="Nada por borrar" pestana="acceso"
        items={borrar.map((e) => [e, e.paraBorrar ? 'pasaron 6 meses' : 'terminó la gracia de la baja'])}
        pie="El borrado sigue pidiendo código por correo." />
      {denuncias > 0 && (
        <section style={{ background: '#fff', border: `1px solid ${C.line}`, borderLeft: '4px solid #A32A2A', borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontWeight: 600, fontSize: 14.5 }}>Denuncias del foro</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, fontWeight: 700 }}>{denuncias}</span>
          </div>
          <div style={{ fontSize: 12, color: C.muted }}>Siguen visibles mientras nadie las revise.</div>
          <button onClick={() => onSeccion('foro')} style={{ alignSelf: 'flex-start', ...botonSuave }}>Revisar en Foro ›</button>
        </section>
      )}
      {usoIa?.credito != null && (
        <section style={{ background: '#fff', border: `1px solid ${C.line}`, borderLeft: `4px solid ${creditoBajo ? '#D9A23A' : C.blue2}`, borderRadius: 12, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontWeight: 600, fontSize: 14.5 }}>Crédito de Anthropic</span>
            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 26, fontWeight: 700, color: creditoBajo ? '#8A5A00' : C.navy }}>{usoIa.diasRestantes != null ? `${usoIa.diasRestantes} días` : '—'}</span>
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>Quedan ≈ {usd(usoIa.restanteEstimado ?? 0)} de {usd(usoIa.credito)} al ritmo de los últimos 30 días.</div>
          <button onClick={() => onSeccion('chatbularis')} style={{ alignSelf: 'flex-start', ...botonSuave }}>Ver consumo ›</button>
        </section>
      )}
    </div>

    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.muted, margin: '30px 0 10px' }}>El negocio</div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, overflow: 'hidden' }}>
      {cifra('Ingreso mensual', clp(mensual), 'equivalente a un mes, de los activos')}
      {cifra('Estudios activos', String(activos.length), `${activos.filter((e) => e.estado === 'TRIAL').length} en prueba · ${activos.filter((e) => e.estado !== 'TRIAL' && !cobrable(e)).length} de cortesía`)}
      {cifra('Costo de Chatbularis', usoIa ? usd(usoIa.usdUltimos30dias) : '…', 'últimos 30 días')}
      {cifra('Comisiones por pagar', comisiones == null ? '…' : clp(comisiones), 'a aliados', !!comisiones)}
    </div>
  </>);
};
const botonSuave: React.CSSProperties = { fontSize: 12.5, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${C.line}`, background: '#fff', color: C.blue2, whiteSpace: 'nowrap' };

// ── Ficha del estudio ────────────────────────────────────────────────────
//
// Reemplaza a las cuatro ventanas que colgaban de cada fila (Cuentas, Plan,
// Chatbularis, Baja). Cada pestaña guarda lo suyo: el modal Plan juntaba
// diez decisiones con dos formas distintas de guardar, y era fácil cambiar
// una fecha de paso al tocar los cupos.
type PestanaFicha = 'resumen' | 'plan' | 'ia' | 'cuentas' | 'acceso';
const PESTANAS: [PestanaFicha, string][] = [['resumen', 'Resumen'], ['plan', 'Plan y cobro'], ['ia', 'Chatbularis'], ['cuentas', 'Cuentas'], ['acceso', 'Acceso y datos']];

const FichaEstudio: React.FC<{
  estudio: Estudio; token: string; pestanaInicial: PestanaFicha;
  onCerrar: () => void; onCambio: () => void; onBaja: () => void;
}> = ({ estudio: e, token, pestanaInicial, onCerrar, onCambio, onBaja }) => {
  const [pestana, setPestana] = useState<PestanaFicha>(pestanaInicial);
  const [err, setErr] = useState('');
  useEffect(() => {
    const k = (ev: KeyboardEvent) => { if (ev.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onCerrar]);

  const contrato = [e.conPanel !== false ? 'Panel de gestión' : null,
    e.saldoIaSinTope ? 'Chatbularis sin tope' : 'Chatbularis prepago']
    .filter(Boolean).join(' + ') || '—';
  const avisos = [
    excede(e) && `Tiene ${e.abogados} abogados y ${e.cupos} cupos contratados.`,
    iaDetenido(e) && 'Chatbularis está detenido: no le alcanza el saldo.',
    e.paraBorrar && 'Pasaron 6 meses desde que terminó su plan: corresponde borrar su información.',
    e.reaperturaHasta && `Tiene abierta la descarga hasta el ${fmt(e.reaperturaHasta)}.`,
  ].filter(Boolean) as string[];

  return (<>
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.42)', zIndex: 90 }} />
    <aside role="dialog" aria-label={`Ficha de ${e.nombre}`}
      style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(600px, 100%)', background: '#fff', zIndex: 95, display: 'flex', flexDirection: 'column', boxShadow: '-12px 0 40px rgba(13,27,53,.18)' }}>
      <div style={{ padding: '18px 22px 0', borderBottom: `1px solid ${C.line}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 21, margin: 0 }}>{e.nombre}</h2>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <a href={`https://${e.subdominio}.tabularis.cl`} target="_blank" rel="noreferrer" style={{ color: C.blue2, textDecoration: 'none' }}>{e.subdominio}.tabularis.cl ↗</a>
              <Estado e={e} chico />
            </div>
          </div>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ border: 'none', background: 'none', fontSize: 24, color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div role="tablist" style={{ display: 'flex', gap: 2, marginTop: 14, overflowX: 'auto' }}>
          {PESTANAS.map(([id, t]) => (
            <button key={id} role="tab" aria-selected={pestana === id} onClick={() => { setPestana(id); setErr(''); }}
              style={{ background: 'none', border: 'none', borderBottom: `2px solid ${pestana === id ? C.blue : 'transparent'}`, padding: '9px 11px', fontSize: 13.5, color: pestana === id ? C.blue : C.muted, fontWeight: pestana === id ? 600 : 400, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px 28px' }}>
        {err && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        {pestana === 'resumen' && (<>
          {avisos.length > 0 && (
            <div style={{ background: '#FFF6E5', border: '1px solid #E8C98A', borderRadius: 9, padding: '10px 14px', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
              {avisos.map((a) => <div key={a}>{a}</div>)}
            </div>
          )}
          <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 150px) 1fr', gap: '10px 14px', fontSize: 13.5, margin: 0 }}>
            <dt style={{ color: C.muted }}>Contacto</dt>
            <dd style={{ margin: 0 }}>{e.contactoNombre || '—'}{e.contactoEmail && <div><a href={`mailto:${e.contactoEmail}`} style={{ color: C.blue2, fontSize: 12.5 }}>{e.contactoEmail}</a></div>}</dd>
            <dt style={{ color: C.muted }}>Contrató</dt><dd style={{ margin: 0 }}>{contrato}</dd>
            <dt style={{ color: C.muted }}>Cobro</dt>
            <dd style={{ margin: 0 }}>{cobrable(e) ? <>{clp(e.cobro!.totalClp)} · {CICLOS[e.ciclo || 'mensual'].toLowerCase()}{e.cobro!.meses > 1 && <span style={{ color: C.muted }}> ({clp(e.cobro!.mensualEquivalenteClp)} al mes)</span>}</> : 'Sin cobro'}</dd>
            <dt style={{ color: C.muted }}>Vence</dt><dd style={{ margin: 0 }}>{e.periodoFin ? `${fmt(e.periodoFin)} · ${venceTxt(e.diasRestantes)}` : 'Sin término'}</dd>
            <dt style={{ color: C.muted }}>Equipo</dt>
            <dd style={{ margin: 0 }}>{e.abogados ?? 0} abogados{e.porCupos && !e.cuposIlimitados ? ` de ${e.cupos ?? 0} cupos` : ''} · {e.usuarios} usuarios</dd>
            <dt style={{ color: C.muted }}>Casos</dt><dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{e.casos}</dd>
            <dt style={{ color: C.muted }}>Chatbularis</dt><dd style={{ margin: 0 }}><CuotaIA e={e} /></dd>
            {e.aliado && (<><dt style={{ color: C.muted }}>Llegó vía</dt><dd style={{ margin: 0 }}>{e.aliado.codigo} · {e.aliado.nombre}</dd></>)}
            <dt style={{ color: C.muted }}>Registrado</dt><dd style={{ margin: 0 }}>{fmt(e.creado)}</dd>
          </dl>
          {e.conPanel !== false && !deBaja(e) && <BotonBienvenida estudio={e} token={token} onCambio={onCambio} />}
          {e.conPanel !== false && !deBaja(e) && <ModuloContabilidad estudio={e} token={token} onCambio={onCambio} onError={setErr} />}
          {cobrable(e) && !deBaja(e) && (
            <div style={{ marginTop: 20, padding: '14px 16px', border: `1px solid ${C.line}`, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>¿Llegó su transferencia?<br />Queda activo y se le suma el ciclo pagado.</div>
              <BotonPago estudio={e} token={token} onCambio={onCambio} onError={setErr} grande />
            </div>
          )}
        </>)}

        {pestana === 'plan' && <PanelPlan estudio={e} token={token} parte="plan" onCambio={onCambio} />}

        {pestana === 'ia' && <PanelSaldoIA estudio={e} token={token} onCambio={onCambio} />}

        {pestana === 'cuentas' && <PanelCuentas estudio={e} token={token} />}

        {pestana === 'acceso' && (<>
          <PanelPlan estudio={e} token={token} parte="acceso" onCambio={onCambio} />
          <ZonaRiesgo estudio={e} token={token} onCambio={onCambio} onError={setErr} onBaja={onBaja} />
        </>)}
      </div>
    </aside>
  </>);
};

/** Correo de bienvenida: cómo traer sus casos con Importar, con el video, y
 *  dónde están los demás videos. Se mira antes de mandarlo, tal como le va a
 *  llegar, y queda anotado cuándo se mandó para no repetirlo. */
/** Contabilidad (liquidaciones, contratos y anexos de sus empleados) se
 *  prueba con un estudio antes de abrirlo a todos: se prende acá, uno a uno. */
const ModuloContabilidad: React.FC<{ estudio: Estudio; token: string; onCambio: () => void; onError: (m: string) => void }> = ({ estudio: e, token, onCambio, onError }) => {
  const prendido = !!e.modulos?.includes('contabilidad');
  const [ocupado, setOcupado] = useState(false);
  const cambiar = async () => {
    setOcupado(true); onError('');
    try { await api(`/plataforma/estudios/${e.id}/modulos`, { method: 'PUT', body: { contabilidad: !prendido }, token }); onCambio(); }
    catch (er: any) { onError(er?.message || 'No se pudo cambiar el módulo.'); }
    finally { setOcupado(false); }
  };
  return (
    <div style={{ marginTop: 14, padding: '14px 16px', border: `1px solid ${C.line}`, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
        <strong style={{ color: C.navy, display: 'block' }}>Contabilidad (en prueba)</strong>
        {prendido ? 'Prendido: sus administradores ven el módulo en el menú.' : 'Liquidaciones, contratos y anexos de sus empleados.'}
      </div>
      <button onClick={cambiar} disabled={ocupado} style={botonSuave}>{ocupado ? '…' : prendido ? 'Apagar' : 'Prender'}</button>
    </div>
  );
};

const BotonBienvenida: React.FC<{ estudio: Estudio; token: string; onCambio: () => void }> = ({ estudio: e, token, onCambio }) => {
  const [abierto, setAbierto] = useState(false);
  return (<>
    <div style={{ marginTop: 20, padding: '14px 16px', border: `1px solid ${C.line}`, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
        <strong style={{ color: C.navy, display: 'block' }}>Correo de bienvenida</strong>
        {e.bienvenidaEnviada ? `Enviado el ${fmt(e.bienvenidaEnviada)}.` : 'Cómo traer sus casos con Importar, con el video.'}
      </div>
      <button onClick={() => setAbierto(true)} style={botonSuave}>{e.bienvenidaEnviada ? 'Ver y reenviar…' : 'Ver y enviar…'}</button>
    </div>
    {abierto && <DialogoCorreo titulo="Correo de bienvenida" ruta={`/plataforma/estudios/${e.id}/bienvenida`} quien="estudio"
      principal="manual" token={token} onEnviado={onCambio} onCerrar={() => setAbierto(false)} />}
  </>);
};

/** Vista previa de un correo de persona armado por el servidor (GET `ruta`),
 *  con dos salidas: copiarlo y mandarlo desde Gmail y anotarlo ("Ya lo
 *  envié"), o mandarlo por Tabularis (SES). Por SES, Gmail suele dejarlo en
 *  Promociones (12-oct-2026); `principal` dice cuál de las dos va destacada. */
const DialogoCorreo: React.FC<{
  titulo: string; ruta: string; quien: string; principal: 'manual' | 'tabularis';
  token: string; onEnviado: () => void; onCerrar: () => void;
}> = ({ titulo, ruta, quien, principal, token, onEnviado, onCerrar }) => {
  const [vista, setVista] = useState<{ para: string | null; subject: string; text: string; html: string; correoHabilitado: boolean } | null>(null);
  const [copiado, setCopiado] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [enviada, setEnviada] = useState('');
  useEffect(() => {
    api<any>(ruta, { token }).then(setVista).catch((er: any) => setError(er?.message || 'No se pudo armar el correo.'));
  }, [ruta, token]);
  // Se copia como HTML y como texto a la vez: pegado en Gmail conserva los
  // enlaces en vez de mostrar las direcciones completas.
  const copiar = async (que: 'para' | 'asunto' | 'correo') => {
    if (!vista) return;
    try {
      if (que === 'correo') {
        const cuerpo = vista.html.replace(/^[\s\S]*?<body[^>]*>|<\/body>[\s\S]*$/g, '');
        await navigator.clipboard.write([new ClipboardItem({
          'text/html': new Blob([cuerpo], { type: 'text/html' }),
          'text/plain': new Blob([vista.text], { type: 'text/plain' }),
        })]);
      } else await navigator.clipboard.writeText(que === 'para' ? vista.para || '' : vista.subject);
      setCopiado(que);
    } catch { setError('El navegador no dejó copiar. Selecciona el texto en la vista previa y cópialo a mano.'); }
  };
  const enviar = async (manual: boolean) => {
    setOcupado(true); setError('');
    try {
      const r = await api<{ enviadoA: string }>(ruta, { method: 'POST', token, ...(manual ? { body: { manual: true } } : {}) });
      setEnviada(manual ? `Anotado: enviado a ${r.enviadoA}` : `Enviado a ${r.enviadoA}`); onEnviado();
    } catch (er: any) { setError(er?.message || 'No se pudo enviar.'); }
    finally { setOcupado(false); }
  };
  const destacado = (deshabilitado: boolean): React.CSSProperties => ({ padding: '9px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: deshabilitado ? 0.6 : 1 });
  const sinPara = !vista?.para || ocupado;
  return (<>
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.42)', zIndex: 100 }} />
    <div role="dialog" aria-label={titulo}
      style={{ position: 'fixed', top: '4vh', left: '50%', transform: 'translateX(-50%)', width: 'min(680px, calc(100% - 24px))', maxHeight: '92vh', background: '#fff', borderRadius: 12, zIndex: 105, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(13,27,53,.3)' }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.line}`, fontSize: 13, lineHeight: 1.6 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <strong style={{ fontSize: 15 }}>{titulo}</strong>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ border: 'none', background: 'none', fontSize: 22, color: C.muted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {vista && (<>
          <div><span style={{ color: C.muted }}>Para:</span> {vista.para || <span style={{ color: '#A32A2A' }}>sin correo de contacto</span>}</div>
          <div><span style={{ color: C.muted }}>Asunto:</span> {vista.subject}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            {([['para', `Copiar correo del ${quien}`], ['asunto', 'Copiar asunto'], ['correo', 'Copiar el correo']] as const).map(([k, t]) => (
              <button key={k} onClick={() => copiar(k)} disabled={k === 'para' && !vista.para} style={botonSuave}>{copiado === k ? '✓ Copiado' : t}</button>
            ))}
          </div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>
            {principal === 'manual'
              ? 'Pégalo en un correo nuevo de Gmail desde jtorruella@tabularis.cl: enviado por ti llega a Principal; por Tabularis, Gmail lo deja en Promociones.'
              : 'Sale por Tabularis desde jtorruella@tabularis.cl y las respuestas te llegan a ti. Gmail puede dejarlo en Promociones: si prefieres, cópialo y mándalo desde tu Gmail.'}
          </div>
        </>)}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', background: '#eef1f7' }}>
        {vista ? <iframe title="Vista previa" srcDoc={vista.html} sandbox="allow-popups allow-popups-to-escape-sandbox" style={{ width: '100%', height: '62vh', border: 'none', display: 'block' }} />
          : !error && <div style={{ padding: 30, color: C.muted, fontSize: 13 }}>Armando el correo…</div>}
      </div>
      <div style={{ padding: '12px 18px', borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {error && <span style={{ color: '#A32A2A', fontSize: 13, marginRight: 'auto' }}>{error}</span>}
        {enviada ? <>
          <span style={{ color: C.ok, fontSize: 13, marginRight: 'auto' }}>✓ {enviada}</span>
          <button onClick={onCerrar} style={botonSuave}>Cerrar</button>
        </> : principal === 'manual' ? <>
          <button onClick={() => enviar(false)} disabled={sinPara} title="Sale por Tabularis (SES): Gmail suele dejarlo en Promociones"
            style={{ ...botonSuave, marginRight: 'auto', color: C.muted }}>
            Enviar por Tabularis
          </button>
          <button onClick={onCerrar} style={botonSuave}>Cancelar</button>
          <button onClick={() => enviar(true)} disabled={sinPara} style={destacado(sinPara)}>{ocupado ? 'Guardando…' : 'Ya lo envié'}</button>
        </> : <>
          <button onClick={() => enviar(true)} disabled={sinPara} title="Lo mandaste tú desde Gmail: solo queda anotado"
            style={{ ...botonSuave, marginRight: 'auto', color: C.muted }}>
            Ya lo envié desde Gmail
          </button>
          <button onClick={onCerrar} style={botonSuave}>Cancelar</button>
          <button onClick={() => enviar(false)} disabled={sinPara} style={destacado(sinPara)}>{ocupado ? 'Enviando…' : 'Enviar'}</button>
        </>}
      </div>
    </div>
  </>);
};

/** Registrar un pago: renueva por el ciclo contratado (el semestral suma seis
 *  meses y el anual doce) y lo deja ACTIVO. Es el botón que convierte una
 *  transferencia recibida en acceso. */
const BotonPago: React.FC<{ estudio: Estudio; token: string; onCambio: () => void; onError: (m: string) => void; grande?: boolean }> = ({ estudio, token, onCambio, onError, grande }) => {
  const [ocupado, setOcupado] = useState(false);
  const meses = estudio.cobro?.meses ?? 1;
  const registrar = async () => {
    if (!window.confirm(
      `¿Registrar un pago de ${estudio.nombre}?\n\n` +
      `Queda ACTIVO y se le suma ${meses} ${meses === 1 ? 'mes' : 'meses'}${estudio.cobro ? ` (${clp(estudio.cobro.totalClp)})` : ''}.` +
      `${estudio.aliado ? `\n\nEste estudio llegó vía ${estudio.aliado.codigo}: se devenga su comisión.` : ''}`)) return;
    setOcupado(true); onError('');
    try { await api(`/plataforma/estudios/${estudio.id}`, { method: 'PATCH', body: { estado: 'ACTIVO', renovarCiclo: true }, token }); onCambio(); }
    catch (e: any) { onError(e?.message || 'No se pudo registrar el pago.'); }
    finally { setOcupado(false); }
  };
  return grande ? (
    <button onClick={registrar} disabled={ocupado} style={{ padding: '10px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
      {ocupado ? 'Registrando…' : `Registrar pago · +${meses} ${meses === 1 ? 'mes' : 'meses'}`}
    </button>
  ) : (
    <button onClick={registrar} disabled={ocupado} style={botonSuave}>{ocupado ? '…' : 'Registrar pago'}</button>
  );
};

/** Suspender y dar de baja, al final de "Acceso y datos" y en rojo: no son
 *  acciones que se aprietan por error entre dos botones azules. */
const ZonaRiesgo: React.FC<{ estudio: Estudio; token: string; onCambio: () => void; onError: (m: string) => void; onBaja: () => void }> = ({ estudio, token, onCambio, onError, onBaja }) => {
  const [ocupado, setOcupado] = useState(false);
  const suspendido = estudio.estado === 'SUSPENDIDO';
  const cambiar = async () => {
    if (!suspendido && !window.confirm(`¿Suspender ${estudio.nombre}? Pierde el acceso de inmediato, incluso con la sesión abierta.`)) return;
    setOcupado(true); onError('');
    try { await api(`/plataforma/estudios/${estudio.id}`, { method: 'PATCH', body: { estado: suspendido ? 'ACTIVO' : 'SUSPENDIDO' }, token }); onCambio(); }
    catch (e: any) { onError(e?.message || 'No se pudo aplicar el cambio.'); }
    finally { setOcupado(false); }
  };
  const fila: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: 13 };
  const rojo: React.CSSProperties = { fontSize: 12.5, padding: '6px 12px', borderRadius: 7, cursor: 'pointer', border: '1px solid #E8C4C4', background: '#fff', color: '#A32A2A', whiteSpace: 'nowrap' };
  return (
    <div style={{ marginTop: 24, border: '1px solid #E8C4C4', background: '#FFFBFB', borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#A32A2A' }}>Zona de riesgo</div>
      {!deBaja(estudio) && (
        <div style={fila}>
          <div style={{ color: C.muted, lineHeight: 1.45 }}><strong style={{ color: C.navy, display: 'block' }}>{suspendido ? 'Reactivar acceso' : 'Suspender acceso'}</strong>{suspendido ? 'Vuelve a entrar con su plan tal como estaba.' : 'Pierde el acceso de inmediato, incluso con la sesión abierta.'}</div>
          <button onClick={cambiar} disabled={ocupado} style={suspendido ? { ...rojo, color: C.blue2, borderColor: C.line } : rojo}>{ocupado ? '…' : suspendido ? 'Reactivar' : 'Suspender'}</button>
        </div>
      )}
      <div style={fila}>
        <div style={{ color: C.muted, lineHeight: 1.45 }}>
          <strong style={{ color: C.navy, display: 'block' }}>{listoParaBorrar(estudio) && estudio.bajaProgramada ? 'Borrar definitivamente' : estudio.bajaProgramada ? 'Baja en curso' : 'Dar de baja'}</strong>
          {estudio.bajaProgramada
            ? (estudio.diasParaBorrar ?? 1) > 0 ? `Faltan ${estudio.diasParaBorrar} días para poder borrarlo. Se puede revertir.` : 'Terminó la gracia: ya se puede borrar.'
            : '30 días de gracia y código por correo antes de borrar nada.'}
        </div>
        <button onClick={onBaja} style={rojo}>{estudio.bajaProgramada ? 'Abrir baja…' : 'Dar de baja…'}</button>
      </div>
    </div>
  );
};

// ── Consumo del redactor con IA ────────────────────────────────────────
// Anthropic no expone el saldo de la cuenta por API (se comprobó contra la API
// real: los endpoints de organización rechazan la llave normal y ninguna
// cabecera trae el crédito). Así que lo que se muestra es medido acá: tokens
// reales de cada llamada, valorizados con los precios configurados.
type UsoIA = {
  /** Un par de precios por modelo: el redactor corre en dos desde que el
   *  abogado elige el razonamiento, y cada llamada se valoriza con el suyo. */
  precios: { modelo: string; entradaUsdPorMTok: number; salidaUsdPorMTok: number }[];
  credito: number | null; creditoDesde: string | null;
  gastadoDesdeCarga: number; restanteEstimado: number | null;
  usdUltimos30dias: number; usdPorDia: number; diasRestantes: number | null;
  llamadasTotales: number; llamadasFallidas: number;
  porMes: { mes: string; llamadas: number; entrada: number; salida: number; usd: number }[];
  /** Por estudio: el total del período y el del mes en curso (hora de Chile). */
  porEstudio: { id: string; nombre: string; llamadas: number; usd: number; usdMes: number; porMes: Record<string, number> }[];
  /** Tipo de cambio de la cuota, para mostrar también en pesos. */
  usdClp: number;
  /** Desde cuándo cuenta el período de "Quién lo usa". */
  periodoDesde: string;
};

const usd = (n: number) => 'US$ ' + n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PanelIA: React.FC<{ token: string }> = ({ token }) => {
  const [d, setD] = useState<UsoIA | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api<UsoIA>('/plataforma/ia', { token }).then(setD).catch(() => setErr('No se pudo cargar el consumo de IA.'));
  }, [token]);

  if (err) return <div style={{ ...caja, color: '#8A2A2A' }}>{err}</div>;
  if (!d) return <div style={{ ...caja, color: C.muted, fontSize: 13 }}>Cargando consumo de IA…</div>;

  // Se avisa cuando quedan menos de tres semanas al ritmo actual: recargar
  // toma minutos, pero enterarse tarde deja el redactor caído para todos.
  const alerta = d.diasRestantes != null && d.diasRestantes < 21;

  return (
    <div style={caja}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 19, margin: 0 }}>Chatbularis IA · consumo</h2>
        <span style={{ fontSize: 12, color: C.muted }}>{d.precios.map(p => p.modelo).join(' · ')}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginTop: 14 }}>
        <Dato titulo="Gasto últimos 30 días" valor={usd(d.usdUltimos30dias)} pie={`${usd(d.usdPorDia)} por día`} />
        <Dato titulo="Llamadas registradas" valor={String(d.llamadasTotales)}
              pie={d.llamadasFallidas ? `${d.llamadasFallidas} sin respuesta útil` : 'todas con respuesta'} />
        {d.credito != null ? (
          <>
            <Dato titulo="Crédito restante (estimado)" valor={usd(d.restanteEstimado ?? 0)}
                  pie={`de ${usd(d.credito)} cargados${d.creditoDesde ? ' el ' + d.creditoDesde : ''}`} alerta={alerta} />
            <Dato titulo="Alcanza para" valor={d.diasRestantes != null ? `${d.diasRestantes} días` : '—'}
                  pie={d.diasRestantes != null ? 'al ritmo de los últimos 30 días' : 'sin consumo aún'} alerta={alerta} />
          </>
        ) : (
          <div style={{ gridColumn: 'span 2', fontSize: 12.5, color: C.muted, lineHeight: 1.6, alignSelf: 'center' }}>
            Para ver cuánto <strong>queda</strong>, anota en el servidor cuánto cargaste
            (<code>IA_CREDITO_USD</code>) y desde qué fecha (<code>IA_CREDITO_DESDE</code>).
            Anthropic no publica el saldo por API: la única forma de estimarlo es
            descontar el gasto medido del monto que cargaste.
          </div>
        )}
      </div>

      {alerta && (
        <div style={{ marginTop: 12, background: '#FFF6E5', border: '1px solid #E8C98A', borderRadius: 8, padding: '9px 12px', fontSize: 13 }}>
          ⚠️ Al ritmo actual el crédito alcanza <strong>{d.diasRestantes} días</strong>. Conviene recargar antes de que Chatbularis deje de responder.
        </div>
      )}

      {d.porMes.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Por mes</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 420 }}>
              <thead><tr style={{ color: C.muted, fontSize: 11.5, textAlign: 'left' }}>
                <th style={celda}>Mes</th><th style={celda}>Llamadas</th>
                <th style={celda}>Tokens entrada</th><th style={celda}>Tokens salida</th><th style={celda}>Costo</th>
              </tr></thead>
              <tbody>{d.porMes.map((m) => (
                <tr key={m.mes} style={{ borderTop: `1px solid ${C.line}` }}>
                  <td style={celda}>{m.mes}</td><td style={celda}>{m.llamadas}</td>
                  <td style={celda}>{m.entrada.toLocaleString('es-CL')}</td>
                  <td style={celda}>{m.salida.toLocaleString('es-CL')}</td>
                  <td style={{ ...celda, fontWeight: 600 }}>{usd(m.usd)}</td>
                </tr>))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {d.porEstudio.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, color: C.muted, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Quién lo usa</div>
          {/* Gasto de cada estudio mes a mes (hora de Chile), del más reciente
              al más antiguo, en pesos al tipo de cambio de la cuota: la misma
              cifra que el estudio ve en "Ver gasto por mes". Los dólares van
              al pasar el mouse. */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 200 + d.porMes.length * 100 }}>
              <thead><tr style={{ color: C.muted, fontSize: 11.5, textAlign: 'left' }}>
                <th style={celda}>Estudio</th>
                {d.porMes.map((m) => <th key={m.mes} style={{ ...celda, textAlign: 'right', whiteSpace: 'nowrap' }}>{nombreMesCorto(m.mes)}</th>)}
                <th style={{ ...celda, textAlign: 'right' }}>Total</th>
              </tr></thead>
              <tbody>
                {d.porEstudio.map((e) => (
                  <tr key={e.id} style={{ borderTop: `1px solid ${C.line}` }}>
                    <td style={celda}>{e.nombre} <span style={{ color: C.muted }}>· {e.llamadas}</span></td>
                    {d.porMes.map((m) => {
                      const v = e.porMes[m.mes] ?? 0;
                      return (
                        <td key={m.mes} title={usd(v)} style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: v ? C.navy : C.line }}>
                          {v ? clp(Math.round(v * d.usdClp)) : '—'}
                        </td>
                      );
                    })}
                    <td title={usd(e.usd)} style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{clp(Math.round(e.usd * d.usdClp))}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: `2px solid ${C.line}` }}>
                  <td style={{ ...celda, fontWeight: 600 }}>Total</td>
                  {d.porMes.map((m) => (
                    <td key={m.mes} title={usd(m.usd)} style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{clp(Math.round(m.usd * d.usdClp))}</td>
                  ))}
                  <td style={{ ...celda, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
                    {clp(Math.round(d.porEstudio.reduce((a, e) => a + e.usd, 0) * d.usdClp))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
            Incluye consultas, lectura de archivos, resúmenes y, desde el 1 de octubre de 2026, las búsquedas web (US$ 10 por cada mil). Antes de esa fecha las búsquedas no se registraban.
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${C.line}`, fontSize: 11.5, color: C.muted, lineHeight: 1.6 }}>
        Cada llamada se valoriza con el precio de SU modelo:{' '}
        {d.precios.map((p, i) => (
          <React.Fragment key={p.modelo}>
            {i > 0 && ' · '}
            {p.modelo}, {usd(p.entradaUsdPorMTok)} por millón de tokens de entrada y {usd(p.salidaUsdPorMTok)} de salida
          </React.Fragment>
        ))}.
        Si no cuadra con tu factura de Anthropic, el precio configurado es el que está mal: se corrige en el servidor y se recalcula todo el histórico,
        porque lo que se guarda son tokens, no montos.
      </div>
    </div>
  );
};

/** "2026-09" → "sep 2026", para las columnas por mes. */
const nombreMesCorto = (m: string) => {
  const [y, mm] = m.split('-').map(Number);
  return new Date(Date.UTC(y, mm - 1, 15)).toLocaleDateString('es-CL', { month: 'short', year: 'numeric', timeZone: 'UTC' }).replace('.', '');
};
const caja: React.CSSProperties = { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '16px 18px', marginTop: 18 };
const celda: React.CSSProperties = { padding: '6px 10px 6px 0' };

const Dato: React.FC<{ titulo: string; valor: string; pie?: string; alerta?: boolean }> = ({ titulo, valor, pie, alerta }) => (
  <div style={{ background: alerta ? '#FFF6E5' : '#F7FAFD', borderRadius: 9, padding: '10px 12px' }}>
    <div style={{ fontSize: 11.5, color: C.muted }}>{titulo}</div>
    <div style={{ fontSize: 20, fontWeight: 700, margin: '2px 0', color: alerta ? '#8A5A00' : C.navy }}>{valor}</div>
    {pie && <div style={{ fontSize: 11.5, color: C.muted }}>{pie}</div>}
  </div>
);

type UsuarioEstudio = { id: string; nombre: string; email: string; rol: string; activo: boolean; emailVerificado: boolean };

// Panel de cuentas de un estudio. Es lo que se abre cuando alguien escribe
// "no puedo entrar": desde acá se le manda el enlace para definir su clave.
const PanelCuentas: React.FC<{ estudio: Estudio; token: string }> = ({ estudio, token }) => {
  const [usuarios, setUsuarios] = useState<UsuarioEstudio[] | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [ocupado, setOcupado] = useState('');
  const [enlace, setEnlace] = useState<{ url: string; email: string; enviado: boolean } | null>(null);

  const recargar = useCallback(async () => {
    try { setUsuarios(await api<UsuarioEstudio[]>(`/plataforma/estudios/${estudio.id}/usuarios`, { token })); }
    catch { setErr('No se pudieron cargar las cuentas.'); }
  }, [estudio.id, token]);
  useEffect(() => { recargar(); }, [recargar]);

  const recuperar = async (u: UsuarioEstudio) => {
    if (!window.confirm(`¿Enviar a ${u.email} un enlace para definir su contraseña?`)) return;
    setOcupado(u.id); setMsg(''); setErr(''); setEnlace(null);
    try {
      const r = await api<{ correoEnviado: boolean; enlace: string; horasValidez: number }>(
        `/plataforma/estudios/${estudio.id}/usuarios/${u.id}/recuperacion`, { method: 'POST', token });
      // El enlace se muestra siempre: si el correo no llegó es la única forma
      // de desatascar a esa persona, y si llegó sirve igual para dictárselo
      // por teléfono cuando no lo encuentra en su bandeja.
      setEnlace({ url: r.enlace, email: u.email, enviado: r.correoEnviado });
      setMsg(r.correoEnviado
        ? `Enlace enviado a ${u.email}. Vence en ${r.horasValidez} horas.`
        : `No se pudo enviar el correo a ${u.email}, pero el enlace ya es válido: pásaselo tú.`);
    } catch (e: any) { setErr(e?.message || 'No se pudo generar el enlace.'); }
    finally { setOcupado(''); }
  };

  const alternar = async (u: UsuarioEstudio) => {
    const accion = u.activo ? 'Desactivar' : 'Reactivar';
    if (!window.confirm(`¿${accion} la cuenta de ${u.email}?`)) return;
    setOcupado(u.id); setMsg(''); setErr('');
    try {
      await api(`/plataforma/estudios/${estudio.id}/usuarios/${u.id}`, { method: 'PATCH', token, body: { activo: !u.activo } });
      await recargar();
    } catch (e: any) { setErr(e?.message || 'No se pudo aplicar.'); }
    finally { setOcupado(''); }
  };

  const bt: React.CSSProperties = { fontSize: 12, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${C.line}`, background: '#fff', color: C.navy, whiteSpace: 'nowrap' };
  return (
    <div>
        <p style={{ fontSize: 12.5, color: C.muted, background: '#F5F8FC', border: `1px solid ${C.line}`, borderRadius: 8, padding: '10px 12px', margin: 0 }}>
          Las contraseñas están cifradas y nadie puede verlas, ni tú. Para ayudar a alguien que no puede entrar, mándale el enlace: él define la suya.
        </p>

        {msg && <div style={{ background: '#E7F4E9', color: '#1B6B2B', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{msg}</div>}
        {err && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{err}</div>}

        {enlace && (
          <div style={{ background: enlace.enviado ? '#F4F6F8' : '#FFF6E5', border: `1px solid ${enlace.enviado ? '#DDE3E9' : '#E8C98A'}`,
                        borderRadius: 8, padding: '10px 12px', marginTop: 12 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>
              Enlace para {enlace.email}{enlace.enviado ? ' (también le llegó por correo)' : ' — el correo no salió, pásaselo tú'}
            </div>
            <input readOnly value={enlace.url} onFocus={(e) => e.currentTarget.select()}
                   style={{ width: '100%', fontSize: 12, fontFamily: 'monospace', padding: '7px 9px',
                            border: '1px solid #DDE3E9', borderRadius: 6, background: '#FFF' }} />
            <button onClick={() => { navigator.clipboard?.writeText(enlace.url); setMsg('Enlace copiado.'); }}
                    style={{ marginTop: 7, fontSize: 12, padding: '5px 10px', borderRadius: 6,
                             border: '1px solid #DDE3E9', background: '#FFF', cursor: 'pointer' }}>
              Copiar enlace
            </button>
          </div>
        )}

        <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
          {!usuarios && <div style={{ color: C.muted, fontSize: 14 }}>Cargando…</div>}
          {usuarios?.map((u) => (
            <div key={u.id} style={{ border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px', opacity: u.activo ? 1 : 0.6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14.5 }}>{u.nombre}</div>
                  <div style={{ fontSize: 13, color: C.blue2 }}>{u.email}</div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>
                    {u.rol}{!u.activo && ' · desactivada'}{!u.emailVerificado && ' · correo sin verificar'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <button style={{ ...bt, borderColor: C.blue2, color: C.blue2 }} disabled={!!ocupado} onClick={() => recuperar(u)}>
                    {ocupado === u.id ? '…' : 'Enviar enlace'}
                  </button>
                  <button style={bt} disabled={!!ocupado} onClick={() => alternar(u)}>
                    {u.activo ? 'Desactivar' : 'Reactivar'}
                  </button>
                </div>
              </div>
            </div>
          ))}
          {usuarios && usuarios.length === 0 && <div style={{ color: C.muted, fontSize: 14 }}>Este estudio no tiene cuentas.</div>}
        </div>
    </div>
  );
};

const CambiarClave: React.FC<{ token: string; onCerrar: () => void }> = ({ token, onCerrar }) => {
  const [actual, setActual] = useState(''); const [nueva, setNueva] = useState(''); const [rep, setRep] = useState('');
  const [err, setErr] = useState(''); const [ok, setOk] = useState(false); const [cargando, setCargando] = useState(false);
  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault(); setErr('');
    if (nueva !== rep) { setErr('Las contraseñas nuevas no coinciden.'); return; }
    setCargando(true);
    try { await api('/plataforma/mi-password', { method: 'POST', token, body: { actual, nueva } }); setOk(true); }
    catch (e: any) { setErr(e?.message || 'No se pudo cambiar.'); }
    finally { setCargando(false); }
  };
  const inp: React.CSSProperties = { width: '100%', padding: '10px 12px', border: `1px solid ${C.line}`, borderRadius: 9, fontSize: 14, boxSizing: 'border-box' };
  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
      <form onClick={(ev) => ev.stopPropagation()} onSubmit={enviar} style={{ background: '#fff', borderRadius: 14, padding: '26px 24px', width: 'min(400px, 100%)', display: 'grid', gap: 12 }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, margin: 0 }}>Cambiar mi contraseña</h2>
        {ok ? (
          <>
            <div style={{ background: '#E7F4E9', color: '#1B6B2B', borderRadius: 8, padding: '10px 12px', fontSize: 13.5 }}>Contraseña actualizada.</div>
            <button type="button" onClick={onCerrar} style={{ padding: '10px 16px', borderRadius: 9, border: 'none', background: C.navy, color: '#fff', cursor: 'pointer', fontSize: 14 }}>Cerrar</button>
          </>
        ) : (
          <>
            <ClaveInput style={inp} placeholder="Contraseña actual" value={actual} onChange={(e) => setActual(e.target.value)} required />
            <ClaveInput style={inp} placeholder="Nueva (mínimo 12 caracteres)" value={nueva} onChange={(e) => setNueva(e.target.value)} required minLength={12} />
            <ClaveInput style={inp} placeholder="Repite la nueva" value={rep} onChange={(e) => setRep(e.target.value)} required />
            {err && <div style={{ color: '#8A2A2A', fontSize: 13 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" disabled={cargando} style={{ padding: '10px 16px', borderRadius: 9, border: 'none', background: C.navy, color: '#fff', cursor: 'pointer', fontSize: 14, opacity: cargando ? 0.6 : 1 }}>{cargando ? 'Guardando…' : 'Guardar'}</button>
              <button type="button" onClick={onCerrar} style={{ padding: '10px 16px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

const Pastilla: React.FC<{ estado: string }> = ({ estado }) => {
  const c: Record<string, [string, string]> = {
    ACTIVO: ['#E7F4E9', '#1B6B2B'], TRIAL: ['#EAF1FB', C.blue],
    SUSPENDIDO: ['#FDF0E7', '#8A4A00'], CANCELADO: ['#FDEDED', '#8A2A2A'],
  };
  const [bg, fg] = c[estado] || ['#EEE', '#555'];
  return <span style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 20, background: bg, color: fg, fontWeight: 600 }}>{estado}</span>;
};

// Plan del estudio, partido en dos pestañas de la ficha. Cada parte manda
// solo lo suyo al guardar:
//   plan   — si tiene panel, plan del panel, cupos, ciclo, precio y aliado.
//   acceso — fecha de término, días de gracia y reapertura para descargar.
// Chatbularis no es parte del plan: es prepago (ver PanelSaldoIA).
// El precio lo calcula el servidor: la consola no interpreta tramos ni
// descuentos, así que no puede quedar diciendo un precio que no es.
const PanelPlan: React.FC<{ estudio: Estudio; token: string; parte: 'plan' | 'ia' | 'acceso'; onCambio: () => void }> = ({ estudio, token, parte, onCambio }) => {
  const [planes, setPlanes] = useState<Plan[] | null>(null);
  const [planId, setPlanId] = useState(estudio.planId || '');
  const [cupos, setCupos] = useState(estudio.cupos || Math.max(1, estudio.abogados || 1));
  const [ciclo, setCiclo] = useState(estudio.ciclo || 'mensual');
  // Con o sin panel. Chatbularis lo tienen todos, prepago: no es parte del plan.
  const [conPanel, setConPanel] = useState(estudio.conPanel !== false);
  const [aliadoCodigo, setAliadoCodigo] = useState(estudio.aliado?.codigo ?? '');
  const [precio, setPrecio] = useState<number>(estudio.precioClp ?? 0);
  // Fecha de término y plazo de recuperación se comparan contra lo que venía
  // para mandar solo lo que cambió.
  const finOriginal = diaChile(estudio.periodoFin);
  const graciaOriginal = estudio.graciaDias == null ? '' : String(estudio.graciaDias);
  const [fin, setFin] = useState(finOriginal);
  const [gracia, setGracia] = useState(graciaOriginal);
  const [reapertura, setReapertura] = useState(diaChile(estudio.reaperturaHasta));
  // Lo que está guardado, aparte de lo que se está escribiendo.
  const [abiertaHasta, setAbiertaHasta] = useState(diaChile(estudio.reaperturaHasta));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [cobro, setCobro] = useState<Cobro | null>(estudio.cobro || null);

  useEffect(() => { api<Plan[]>('/plataforma/planes', { token }).then(setPlanes).catch(() => setPlanes([])); }, [token]);
  const plan = planes?.find((p) => p.id === planId);
  const esCortesia = !!plan?.cuposIlimitados;
  const esPromocion = !!plan?.precioAcordado;

  const llamar = async (body: any, mensajeOk?: string) => {
    setGuardando(true); setError(''); setAviso('');
    try {
      const r = await api<{ cobro: Cobro | null }>(`/plataforma/estudios/${estudio.id}`, { method: 'PATCH', token, body });
      setCobro(r.cobro);
      setAviso(mensajeOk || 'Guardado.');
      onCambio();
      return true;
    } catch (e: any) { setError(e?.message || 'No se pudo guardar.'); return false; }
    finally { setGuardando(false); }
  };

  const graciaDefecto = estudio.graciaDiasDefecto ?? 3;
  const diasGracia = gracia === '' ? graciaDefecto : Math.max(0, Math.min(365, Number(gracia) || 0));
  // Día en que se cierra del todo. La aritmética va en UTC sobre la fecha
  // pelada para no depender de la zona del navegador.
  const cierre = fin
    ? new Date(Date.parse(`${fin}T00:00:00Z`) + diasGracia * 86400000).toLocaleDateString('es-CL', { timeZone: 'UTC' })
    : null;

  // El plazo de rescate lo calcula el servidor desde el término del plan.
  const hoyChile = diaChile(new Date().toISOString());
  const topeReapertura = estudio.rescateHasta ?? '';
  const rescateVencido = !!topeReapertura && topeReapertura < hoyChile;
  const enMeses = (n: number) => {
    const [y, m, d] = hoyChile.split('-').map(Number);
    const candidato = new Date(Date.UTC(y, m - 1 + n, d)).toISOString().slice(0, 10);
    return topeReapertura && candidato > topeReapertura ? topeReapertura : candidato;
  };
  const abierta = !!abiertaHasta;

  const guardarPlan = () => llamar({
    planId, ciclo, conPanel,
    ...(esCortesia ? {} : { cupos }),
    ...(esPromocion ? { precioClp: Math.max(0, Math.round(precio)) } : {}),
  });
  const cambiaAcceso = fin !== finOriginal || gracia !== graciaOriginal;
  const guardarAcceso = () => llamar({
    ...(fin !== finOriginal ? { periodoFin: fin || null } : {}),
    ...(gracia !== graciaOriginal ? { graciaDias: gracia === '' ? null : diasGracia } : {}),
  });

  const segmento = (activo: boolean): React.CSSProperties => ({
    flex: 1, padding: '9px 8px', borderRadius: 9, fontSize: 13, cursor: 'pointer',
    border: `1px solid ${activo ? C.blue2 : C.line}`, background: activo ? '#EEF4FD' : '#fff',
    color: activo ? C.blue2 : C.navy, fontWeight: activo ? 700 : 400,
  });
  const ayuda: React.CSSProperties = { fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.55 };
  const selectorCiclo = (
    <div style={{ display: 'flex', gap: 8 }}>
      {Object.entries(CICLOS).map(([k, etiqueta]) => (
        <button key={k} onClick={() => setCiclo(k)} style={segmento(ciclo === k)}>{etiqueta}</button>
      ))}
    </div>
  );
  const mensajes = (<>
    {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{error}</div>}
    {aviso && <div style={{ background: '#EAF6EE', color: '#1B5E20', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{aviso}</div>}
  </>);
  const botonGuardar = (accion: () => void, texto: string, deshabilitado = false) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
      <button onClick={accion} disabled={guardando || deshabilitado}
        style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', cursor: guardando ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600, opacity: deshabilitado ? 0.5 : 1 }}>
        {guardando ? 'Guardando…' : texto}
      </button>
    </div>
  );

  if (parte === 'plan') return (
    <div>
      <p style={{ color: C.muted, fontSize: 13, marginTop: 0, lineHeight: 1.55 }}>
        El panel se cobra por plan: 1 abogado, hasta 3 o hasta 5 (los captadores no ocupan cupo y los procuradores vienen incluidos, dos por abogado). Más de 5, con precio acordado.
        Chatbularis es prepago y va aparte: lo tienen todos los estudios, con o sin panel.
      </p>
      <label style={lbl}>Qué tiene</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {([['panel', 'Panel + Chatbularis'], ['ia', 'Solo Chatbularis']] as const).map(([k, etiqueta]) => (
          <button key={k} style={segmento(k === 'panel' ? conPanel : !conPanel)} onClick={() => setConPanel(k === 'panel')}>{etiqueta}</button>
        ))}
      </div>
      {!conPanel && <div style={{ ...ayuda, marginTop: -6, marginBottom: 12 }}>Sin panel no hay mensualidad ni vencimiento: solo el saldo de Chatbularis.</div>}

      {conPanel && (<>
        <label style={lbl}>Plan del panel</label>
        <select style={inp} value={planId} onChange={(e) => setPlanId(e.target.value)}>
          <option value="">— sin plan —</option>
          {(planes || []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}{p.cuposIlimitados ? ' (gratis, sin límite)' : p.precioAcordado ? ' (precio a convenir)' : ' (por cupos)'}
            </option>
          ))}
        </select>
        {esCortesia && (
          <div style={{ background: '#EAF6EE', border: '1px solid #BFE3CB', borderRadius: 8, padding: '12px 14px', fontSize: 13, marginTop: 12, lineHeight: 1.6 }}>
            <strong>Cortesía:</strong> sin costo y sin tope de abogados ni procuradores. No vence sola: dura hasta que le pongas fecha en <em>Acceso y datos</em>.
          </div>
        )}
        {!esCortesia && (<>
          <label style={{ ...lbl, marginTop: 12 }}>Cupos de abogado</label>
          <input style={inp} type="number" min={1} max={500} value={cupos} onChange={(e) => setCupos(Math.max(1, Number(e.target.value) || 1))} />
          <div style={{ ...ayuda, color: excede(estudio) ? '#8A2A2A' : C.muted }}>
            Hoy tiene {estudio.abogados ?? 0} abogado{(estudio.abogados ?? 0) === 1 ? '' : 's'} activo{(estudio.abogados ?? 0) === 1 ? '' : 's'}.
          </div>
        </>)}
      </>)}

      {conPanel && (esPromocion || (!esCortesia && plan)) && (<>
        <label style={{ ...lbl, marginTop: 12 }}>Ciclo de pago</label>
        {selectorCiclo}
      </>)}

      {conPanel && esPromocion && (<>
        <label style={{ ...lbl, marginTop: 12 }}>Precio acordado, por cada cobro</label>
        <input style={inp} type="number" min={0} step={1000} value={precio} onChange={(e) => setPrecio(Math.max(0, Number(e.target.value) || 0))} />
        <div style={ayuda}>
          Es el monto exacto que se le cobra cada {ciclo === 'mensual' ? 'mes' : ciclo === 'semestral' ? 'seis meses' : 'año'},
          sin descuentos automáticos encima: el trato ya es el descuento.
        </div>
      </>)}

      {cobro && cobro.totalClp > 0 && (
        <div style={{ marginTop: 16, background: '#F7FAFD', border: `1px solid ${C.line}`, borderRadius: 10, padding: 14 }}>
          <div style={{ fontSize: 12, color: C.muted }}>A cobrar cada {cobro.meses} {cobro.meses === 1 ? 'mes' : 'meses'}</div>
          <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{clp(cobro.totalClp)}</div>
          <div style={{ fontSize: 12.5, color: C.muted }}>
            Equivale a {clp(cobro.mensualEquivalenteClp)} al mes
            {cobro.ahorroClp > 0 && <> · ahorra <strong style={{ color: C.ok }}>{clp(cobro.ahorroClp)}</strong> frente al mensual</>}
          </div>
        </div>
      )}
      {mensajes}
      {botonGuardar(guardarPlan, 'Guardar plan', !planId)}
      <p style={{ ...ayuda, textAlign: 'right' }}>Guardar el plan no cobra. Para renovar, usa Registrar pago.</p>

      {/* Atribución a un aliado. Es una acción aparte porque mueve a quién se
          le paga, y eso no se cambia de pasada al guardar los cupos. */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: `1px solid ${C.line}` }}>
        <label style={lbl}>Aliado que lo trajo (código)</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...inp, textTransform: 'uppercase' }} value={aliadoCodigo} onChange={(e) => setAliadoCodigo(e.target.value.toUpperCase())} placeholder="Sin aliado" />
          <button onClick={() => llamar({ aliadoCodigo: aliadoCodigo.trim() || null }, aliadoCodigo.trim() ? `Queda atribuido a ${aliadoCodigo.trim().toUpperCase()}.` : 'Queda sin aliado.')}
            disabled={guardando || aliadoCodigo.trim().toUpperCase() === (estudio.aliado?.codigo ?? '')}
            style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', color: C.navy, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Cambiar
          </button>
        </div>
        <div style={ayuda}>
          {estudio.aliado ? <>Hoy: <strong>{estudio.aliado.codigo}</strong> · {estudio.aliado.nombre}. Las comisiones se devengan al registrar cada pago.</> : 'Sin aliado: los pagos de este estudio no generan comisión.'}
        </div>
      </div>
    </div>
  );

  // parte === 'acceso'. Término del plan y salida van juntos porque son la
  // misma conversación: hasta cuándo trabaja y hasta cuándo puede llevarse lo
  // suyo.
  return (
    <div>
      <label style={lbl}>Fecha de término del plan</label>
      <input style={inp} type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
      <div style={ayuda}>Trabaja hasta el final de ese día. Vacío: no vence solo, dura hasta que le pongas fecha.</div>

      <label style={{ ...lbl, marginTop: 12 }}>Días para recuperar la información</label>
      <input style={inp} type="number" min={0} max={365} value={gracia} placeholder={`${graciaDefecto} (el plazo estándar)`}
        onChange={(e) => setGracia(e.target.value.replace(/[^0-9]/g, ''))} />
      <div style={ayuda}>
        Terminado el plan puede entrar a consultar y descargar lo suyo durante ese plazo, sin registrar cambios.
        Vacío deja el estándar de {graciaDefecto} día{graciaDefecto === 1 ? '' : 's'}.
      </div>
      {cierre && (
        <div style={{ fontSize: 12.5, color: C.navy, background: '#F7FAFD', border: `1px solid ${C.line}`, borderRadius: 8, padding: '9px 12px', marginTop: 8 }}>
          {diasGracia === 0
            ? <>Se cierra el mismo <strong>{fmtDia(fin)}</strong>, sin plazo para descargar.</>
            : <>Solo lectura desde el <strong>{fmtDia(fin)}</strong> y sin acceso a partir del <strong>{cierre}</strong>.</>}
        </div>
      )}
      {mensajes}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: 'wrap' }}>
        {esCortesia && finOriginal && (
          <button onClick={() => { setFin(''); llamar({ planId, sinVencimiento: true, estado: 'ACTIVO' }, 'Queda activo y sin fecha de término.'); }}
            disabled={guardando} style={{ padding: '10px 16px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 14, marginRight: 'auto' }}>
            Dejar sin vencimiento
          </button>
        )}
        <button onClick={guardarAcceso} disabled={guardando || !cambiaAcceso}
          style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, opacity: cambiaAcceso ? 1 : 0.5 }}>
          {guardando ? 'Guardando…' : 'Guardar plazos'}
        </button>
      </div>
      <p style={{ ...ayuda, textAlign: 'right' }}>La fecha se escribe tal cual; para renovar sumando el ciclo pagado, usa Registrar pago.</p>

      {/* Reapertura: el estudio ya se fue y vuelve a buscar sus archivos. Es
          una acción aparte porque no cobra ni reactiva, y tiene que poder
          hacerse sobre un estudio suspendido o dado de baja. */}
      <div style={{ marginTop: 18, background: abierta ? '#EAF6EE' : '#F7FAFD', border: `1px solid ${abierta ? '#BFE3CB' : C.line}`, borderRadius: 10, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Reabrir el acceso para descargar</div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.55 }}>
          Le devuelve la entrada en modo lectura (consultar y descargar, sin registrar cambios) aunque esté vencido,
          suspendido o dado de baja. No reactiva el plan ni cobra.
        </div>
        {topeReapertura && (
          <div style={{ fontSize: 12, color: rescateVencido ? '#8A2A2A' : C.navy, marginTop: 6, lineHeight: 1.55 }}>
            {rescateVencido
              ? <>El plazo para rescatar sus datos venció el <strong>{fmtDia(topeReapertura)}</strong>, {estudio.mesesReapertura ?? 6} meses después del término de su plan. Corresponde eliminarlos con Dar de baja.</>
              : <>Puedes reabrirle el acceso hasta el <strong>{fmtDia(topeReapertura)}</strong>: son {estudio.mesesReapertura ?? 6} meses desde que terminó su plan.</>}
          </div>
        )}
        {abierta && <div style={{ fontSize: 13, color: '#1B5E20', marginTop: 8 }}>Abierto hasta el <strong>{fmtDia(abiertaHasta)}</strong>.</div>}
        {!rescateVencido && (<>
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {[1, 3, 6].map((m) => (
              <button key={m} onClick={() => setReapertura(enMeses(m))} style={{ padding: '6px 12px', borderRadius: 8, fontSize: 12.5, cursor: 'pointer', border: `1px solid ${reapertura === enMeses(m) ? C.blue2 : C.line}`, background: reapertura === enMeses(m) ? '#EEF4FD' : '#fff', color: C.blue2 }}>
                {m} {m === 1 ? 'mes' : 'meses'}
              </button>
            ))}
          </div>
          <input style={{ ...inp, marginTop: 8 }} type="date" value={reapertura} max={topeReapertura} onChange={(e) => setReapertura(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button onClick={async () => { if (await llamar({ reaperturaHasta: reapertura }, `Puede entrar a descargar hasta el ${fmtDia(reapertura)}.`)) setAbiertaHasta(reapertura); }}
              disabled={guardando || !reapertura} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: C.blue, color: '#fff', fontSize: 13, fontWeight: 600, cursor: guardando || !reapertura ? 'default' : 'pointer', opacity: reapertura ? 1 : 0.5 }}>
              {abierta ? 'Cambiar la fecha' : 'Abrir acceso'}
            </button>
            {abierta && (
              <button onClick={async () => { if (await llamar({ reaperturaHasta: null }, 'Acceso cerrado.')) { setReapertura(''); setAbiertaHasta(''); } }}
                disabled={guardando} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', color: C.navy, fontSize: 13, cursor: 'pointer' }}>
                Cerrar acceso
              </button>
            )}
          </div>
        </>)}
      </div>
    </div>
  );
};

/* Baja de un estudio. Tres barreras, porque borrar no tiene vuelta:
   escribir el subdominio exacto, un código que llega al correo del dueño, y
   30 días de gracia en los que el estudio queda sin acceso pero con todos sus
   datos. El borrado definitivo recién se habilita después, y pide código otra
   vez. */
const PanelBaja: React.FC<{ estudio: Estudio; token: string; onCerrar: () => void; onCambio: () => void }> = ({ estudio, token, onCerrar, onCambio }) => {
  const enGracia = !!estudio.bajaProgramada;
  const puedeBorrar = enGracia && (estudio.diasParaBorrar ?? 1) <= 0;
  const [paso, setPaso] = useState<'inicio' | 'codigo'>('inicio');
  const [sub, setSub] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviadoA, setEnviadoA] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState('');
  const [listo, setListo] = useState('');

  const llamar = async (ruta: string, body?: any, metodo: 'POST' | 'DELETE' = 'POST') => {
    setOcupado(true); setError('');
    try { return await api<any>(ruta, { method: metodo, token, body }); }
    catch (e: any) { setError(e?.message || 'No se pudo completar.'); return null; }
    finally { setOcupado(false); }
  };

  const pedirCodigo = async () => {
    const r = await llamar(`/plataforma/estudios/${estudio.id}/baja/solicitar`, { subdominio: sub.trim(), definitiva: puedeBorrar });
    if (r) { setEnviadoA(r.enviadoA); setPaso('codigo'); }
  };
  const confirmar = async () => {
    const r = puedeBorrar
      ? await llamar(`/plataforma/estudios/${estudio.id}`, { codigo: codigo.trim() }, 'DELETE')
      : await llamar(`/plataforma/estudios/${estudio.id}/baja/confirmar`, { codigo: codigo.trim() });
    if (r) {
      setListo(puedeBorrar
        ? `Estudio borrado definitivamente${r.documentosBorrados ? ` (${r.documentosBorrados} documentos)` : ''}.`
        : `Baja registrada. Tienes hasta el ${fmt(r.bajaProgramada)} para revertirla.`);
      onCambio();
    }
  };
  const revertir = async () => {
    const r = await llamar(`/plataforma/estudios/${estudio.id}/baja/revertir`);
    if (r) { setListo('Baja revertida. El estudio vuelve a estar activo.'); onCambio(); }
  };

  const rojo = { padding: '10px 18px', borderRadius: 9, border: 'none', background: '#A32A2A', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 100 }}>
      <div onClick={(ev) => ev.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 520 }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, margin: '0 0 2px', color: '#8A2A2A' }}>
          {puedeBorrar ? 'Borrar definitivamente' : 'Dar de baja'} {estudio.nombre}
        </h2>
        <p style={{ color: C.muted, fontSize: 13, marginTop: 2 }}>{estudio.subdominio}.tabularis.cl · {estudio.usuarios} usuarios · {estudio.casos} casos</p>

        {listo ? (
          <>
            <div style={{ background: '#EAF6EE', color: '#1B5E20', borderRadius: 8, padding: '12px 14px', fontSize: 13.5 }}>{listo}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={onCerrar} style={{ padding: '10px 18px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cerrar</button>
            </div>
          </>
        ) : (
          <>
            {enGracia && (
              <div style={{ background: '#FFF7E6', border: '1px solid #F0D9A8', borderRadius: 8, padding: '12px 14px', fontSize: 13, marginBottom: 14 }}>
                Este estudio ya está dado de baja y sin acceso. {puedeBorrar
                  ? <>El período de gracia terminó: <strong>ahora sí se puede borrar para siempre</strong>.</>
                  : <>Faltan <strong>{estudio.diasParaBorrar} días</strong> para poder borrarlo. Hasta entonces conserva todos sus datos y la baja se puede revertir.</>}
              </div>
            )}

            {paso === 'inicio' ? (
              <>
                <div style={{ background: '#FDEDED', border: '1px solid #F3C6C6', borderRadius: 8, padding: '12px 14px', fontSize: 13, lineHeight: 1.6 }}>
                  {puedeBorrar
                    ? <>Se borrarán <strong>para siempre</strong> sus casos, documentos, usuarios y auditoría. No hay forma de recuperarlos.</>
                    : <>El estudio pierde el acceso de inmediato, pero <strong>conserva todos sus datos 30 días</strong>. Durante ese plazo puedes revertirlo.</>}
                </div>
                <label style={{ ...lbl, marginTop: 14 }}>Para continuar, escribe <strong style={{ color: C.navy }}>{estudio.subdominio}</strong></label>
                <input style={inp} value={sub} onChange={(e) => setSub(e.target.value)} placeholder={estudio.subdominio} autoFocus />
                {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 10 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 18, flexWrap: 'wrap' }}>
                  {enGracia && <button onClick={revertir} disabled={ocupado} style={{ padding: '10px 16px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 14 }}>↩ Revertir la baja</button>}
                  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                    <button onClick={onCerrar} style={{ padding: '10px 16px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Cancelar</button>
                    <button onClick={pedirCodigo} disabled={ocupado || sub.trim().toLowerCase() !== estudio.subdominio} style={{ ...rojo, opacity: sub.trim().toLowerCase() === estudio.subdominio ? 1 : 0.4 }}>
                      {ocupado ? 'Enviando…' : 'Enviarme el código'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                  Mandamos un código de 6 dígitos a <strong>{enviadoA}</strong>. Vence en 15 minutos.
                </p>
                <input style={{ ...inp, fontSize: 24, letterSpacing: '0.3em', textAlign: 'center', fontFamily: 'monospace' }}
                  value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" inputMode="numeric" autoFocus />
                {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 10 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                  <button onClick={() => { setPaso('inicio'); setCodigo(''); setError(''); }} style={{ padding: '10px 16px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 14 }}>Atrás</button>
                  <button onClick={confirmar} disabled={ocupado || codigo.length !== 6} style={{ ...rojo, opacity: codigo.length === 6 ? 1 : 0.4 }}>
                    {ocupado ? 'Aplicando…' : puedeBorrar ? 'Borrar para siempre' : 'Confirmar la baja'}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Login: React.FC<{ onLogin: (t: string) => void }> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault(); setError(''); setLoading(true);
    try {
      const r = await api<{ token: string }>('/plataforma/login', { method: 'POST', auth: false, body: { email: email.trim(), password } });
      onLogin(r.token);
    } catch (e: any) {
      setError(e?.status === 401 ? 'Correo o contraseña incorrectos.' : 'No se pudo iniciar sesión.');
      setLoading(false);
    }
  };
  return (
    <div style={{ fontFamily: 'Inter, sans-serif', minHeight: '100vh', background: '#F5F8FC', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, color: C.navy }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.blue, marginBottom: 22 }}>
        <LogoLockup height={32} />
      </div>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 380, background: '#fff', border: `1px solid ${C.line}`, borderRadius: 14, padding: '28px 26px', boxShadow: '0 12px 34px rgba(13,27,53,0.08)' }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, margin: '0 0 4px' }}>Consola del dueño</h1>
        <p style={{ color: C.muted, fontSize: 13.5, marginTop: 0 }}>Acceso privado de administración.</p>
        <label style={lbl}>Correo</label>
        <input style={inp} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
        <label style={{ ...lbl, marginTop: 12 }}>Contraseña</label>
        <ClaveInput style={inp} value={password} onChange={(e) => setPassword(e.target.value)} required />
        {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{error}</div>}
        <button type="submit" disabled={loading} style={{ width: '100%', marginTop: 16, background: C.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '12px', fontSize: 15, fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>{loading ? 'Ingresando…' : 'Ingresar'}</button>
      </form>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; c?: boolean }> = ({ children, c }) => (
  <th style={{ padding: '11px 14px', textAlign: c ? 'center' : 'left', fontWeight: 600 }}>{children}</th>
);
const td: React.CSSProperties = { padding: '11px 14px', textAlign: 'left', verticalAlign: 'middle' };

// ── Saldo de Chatbularis IA de un estudio ────────────────────────────────
type MovimientoIA = { id: string; tipo: string; montoClp: number; saldoClp: number; origen: string | null; detalle: string | null; ts: string };
type CargaIA = { id: string; concepto: string; meses: number | null; requestId: number | null; motivo: string | null; montoClp: number; bonoClp: number; estado: string; medio: string | null; autorizacion: string | null; creado: string; pagada: string | null };
type SaldoEstudio = {
  saldoClp: number;
  /** Sin tope: nunca se detiene y se descuenta al costo. */
  sinTope: boolean;
  agotado: boolean;
  /** Dólar y multiplicador con que se cobra hoy. */
  usdClp: number;
  multiplicador: number;
  movimientos: MovimientoIA[];
  cargas: CargaIA[];
};

/** El saldo prepago de Chatbularis de un estudio: cuánto le queda, sus cargas
 *  por Banchile Pagos, la cartola, y las dos palancas a mano: regalar o descontar saldo
 *  (con motivo) y quitarle el tope. */
const PanelSaldoIA: React.FC<{ estudio: Estudio; token: string; onCambio: () => void }> = ({ estudio, token, onCambio }) => {
  const [d, setD] = useState<SaldoEstudio | null>(null);
  const [monto, setMonto] = useState('');
  const [detalle, setDetalle] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const recargar = useCallback(async () => {
    try { setD(await api<SaldoEstudio>(`/plataforma/estudios/${estudio.id}/saldo`, { token })); }
    catch { setErr('No se pudo cargar el saldo.'); }
  }, [estudio.id, token]);
  useEffect(() => { recargar(); }, [recargar, estudio]);

  const montoClp = Math.round(Number(monto.replace(/[^\d-]/g, '')) || 0);
  const esCorreccion = montoClp < 0;

  const corregir = async () => {
    if (!montoClp || !detalle.trim()) return;
    // La confirmación dice el monto y el estudio: es saldo de un tercero, y un
    // clic de más en la fila equivocada se descubre cuando el otro reclama.
    const verbo = esCorreccion ? 'Descontar' : 'Sumar';
    if (!window.confirm(`¿${verbo} ${clp(Math.abs(montoClp))} de saldo a ${estudio.nombre}?`)) return;
    setOcupado(true); setMsg(''); setErr('');
    try {
      await api(`/plataforma/estudios/${estudio.id}/saldo`, { method: 'POST', token, body: { montoClp, detalle: detalle.trim() } });
      setMonto(''); setDetalle('');
      setMsg('Listo.');
      await recargar();
      onCambio();
    } catch (e: any) { setErr(e?.message || 'No se pudo corregir el saldo.'); }
    finally { setOcupado(false); }
  };

  const cambiarTope = async (sinTope: boolean) => {
    if (!window.confirm(sinTope
      ? `¿Dejar a ${estudio.nombre} sin tope? Nunca se detiene y se le descuenta al costo, no al precio.`
      : `¿Ponerle tope a ${estudio.nombre}? Desde ahora paga cada consulta a precio y se detiene sin saldo.`)) return;
    setOcupado(true); setMsg(''); setErr('');
    try {
      await api(`/plataforma/estudios/${estudio.id}/saldo/tope`, { method: 'PUT', token, body: { sinTope } });
      await recargar();
      onCambio();
    } catch (e: any) { setErr(e?.message || 'No se pudo cambiar el tope.'); }
    finally { setOcupado(false); }
  };

  const nombreMov = (m: MovimientoIA) =>
    m.tipo === 'carga' ? 'Carga' : m.tipo === 'bienvenida' ? 'Bienvenida' : m.tipo === 'ajuste' ? 'Ajuste'
      : m.tipo === 'cuota' ? 'Cuota (plan anterior)' : m.tipo === 'prueba' ? 'Prueba (antes del prepago)'
      : m.origen === 'ocr' ? 'Lectura de archivo' : 'Consulta';
  const estadoCarga: Record<string, string> = { pendiente: 'pendiente', pagada: 'pagada', rechazada: 'rechazada', anulada: 'anulada' };

  return (
    <div>
        {!d ? (
          <div style={{ color: C.muted, fontSize: 14, marginTop: 16 }}>Cargando…</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap', marginTop: 14, paddingBottom: 14, borderBottom: `1px solid ${C.line}` }}>
              <div>
                <div style={{ fontSize: 12, color: C.muted }}>{d.sinTope && d.saldoClp < 0 ? 'Gastado (al costo)' : 'Saldo'}</div>
                <div style={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: d.agotado ? '#8A2A2A' : C.navy }}>{clp(d.sinTope && d.saldoClp < 0 ? -d.saldoClp : d.saldoClp)}</div>
                <div style={{ fontSize: 12, color: C.muted }}>Precio: costo × {clp(d.usdClp)} × {d.multiplicador}</div>
              </div>
              <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: C.muted, lineHeight: 1.6 }}>
                {d.sinTope
                  ? <strong style={{ color: C.blue2 }}>Sin tope: nunca se detiene y se le descuenta al costo. El gasto queda en la cartola.</strong>
                  : <>Prepago: carga solo por Banchile Pagos y cada consulta descuenta su precio.</>}
                {d.agotado && <strong style={{ color: '#8A2A2A' }}> Ahora mismo está detenido.</strong>}
                <div>
                  <button onClick={() => cambiarTope(!d.sinTope)} disabled={ocupado}
                    style={{ marginTop: 6, padding: '5px 10px', borderRadius: 7, border: `1px solid ${C.line}`, background: '#fff', color: C.navy, fontSize: 12, cursor: 'pointer' }}>
                    {d.sinTope ? 'Ponerle tope' : 'Dejar sin tope (cortesía)'}
                  </button>
                </div>
              </div>
            </div>

            <label style={{ ...lbl, marginTop: 14 }}>Sumar o descontar saldo</label>
            <input style={inp} inputMode="numeric" placeholder="Ej: 5000 suma; -800 descuenta" value={monto}
                   onChange={(e) => setMonto(e.target.value)} />
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              Para devolver una consulta que salió mal, regalar saldo o acreditar una carga pagada por transferencia. Siempre con motivo: la cartola tiene que poder explicarlo sola.
            </div>

            <label style={{ ...lbl, marginTop: 12 }}>Motivo (obligatorio)</label>
            <input style={inp} placeholder="Devolución: la consulta se cortó por una falla nuestra" value={detalle}
                   onChange={(e) => setDetalle(e.target.value)} />

            <button onClick={corregir} disabled={ocupado || !montoClp || !detalle.trim()}
                    style={{ marginTop: 14, padding: '11px 18px', borderRadius: 9, border: 'none', cursor: ocupado || !montoClp ? 'default' : 'pointer',
                             background: !montoClp || !detalle.trim() ? '#C7D3E5' : esCorreccion ? '#8A2A2A' : C.blue2, color: '#fff', fontSize: 14, fontWeight: 600 }}>
              {ocupado ? 'Guardando…' : esCorreccion ? `Descontar ${clp(Math.abs(montoClp))}` : montoClp ? `Sumar ${clp(montoClp)}` : 'Corregir'}
            </button>

            {msg && <div style={{ background: '#E7F4E9', color: '#1B6B2B', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{msg}</div>}
            {err && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{err}</div>}

            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.muted, margin: '20px 0 8px' }}>Pagos en línea (Banchile Pagos)</div>
            {d.cargas.length === 0 ? (
              <div style={{ fontSize: 13, color: C.muted }}>Todavía no ha pagado en línea.</div>
            ) : d.cargas.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13, padding: '6px 0', borderTop: `1px solid ${C.line}` }}>
                <span style={{ color: C.navy }}>
                  {c.concepto === 'suscripcion' ? `Mensualidad (${c.meses ?? 1} ${c.meses === 1 ? 'mes' : 'meses'})` : 'Saldo Chatbularis'} · {clp(c.montoClp)}{c.bonoClp ? ` + ${clp(c.bonoClp)} de regalo` : ''}
                  <span style={{ color: C.muted, fontSize: 11.5 }}> · {fmt(c.creado)}{c.medio ? ` · ${c.medio}` : ''}{c.requestId ? ` · N° ${c.requestId}` : ''}{c.autorizacion ? ` · autorización ${c.autorizacion}` : ''}{c.motivo && c.estado !== 'pagada' ? ` · ${c.motivo}` : ''}</span>
                </span>
                <span style={{ color: c.estado === 'pagada' ? C.ok : c.estado === 'pendiente' ? C.muted : '#8A2A2A', fontWeight: 600 }}>{estadoCarga[c.estado] ?? c.estado}</span>
              </div>
            ))}

            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.muted, margin: '20px 0 8px' }}>Movimientos</div>
            {d.movimientos.length === 0 ? (
              <div style={{ fontSize: 13, color: C.muted }}>Todavía no hay movimientos.</div>
            ) : d.movimientos.map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', fontSize: 13, padding: '6px 0', borderTop: `1px solid ${C.line}` }}>
                <span style={{ color: C.navy, minWidth: 0 }}>
                  {nombreMov(m)}
                  <span style={{ color: C.muted, fontSize: 11.5 }}> · {fmt(m.ts)}</span>
                  {m.detalle && <div style={{ color: C.muted, fontSize: 11.5 }}>{m.detalle}</div>}
                </span>
                <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  <span style={{ color: m.montoClp >= 0 ? C.ok : C.navy, fontWeight: m.montoClp >= 0 ? 700 : 400 }}>
                    {m.montoClp >= 0 ? '+' : '−'}{clp(Math.abs(m.montoClp))}
                  </span>
                  <div style={{ color: C.muted, fontSize: 11.5 }}>quedó {clp(m.saldoClp)}</div>
                </span>
              </div>
            ))}
          </>
        )}
    </div>
  );
};

// ── Aliados y comisiones ────────────────────────────────────────────────
// Quién recomienda Tabularis y cuánto se le debe. La lista muestra lo
// pendiente de cada uno; el detalle es el informe que se le manda cada mes y
// el botón que lo marca pagado. Las reglas (atribución al registrarse,
// comisión solo con pago registrado, tope de meses por componente) viven en el
// servidor: acá solo se muestran.
type Aliado = {
  id: string; codigo: string; nombre: string; contacto: string | null;
  pctIa: number; mesesIa: number | null; pctPanel: number; mesesPanel: number | null;
  activo: boolean; notas: string | null; creado: string;
  /** Cuándo se le mandó el correo con su enlace y su trato. */
  instruccionesEnviadas: string | null;
  estudios: number; pendienteClp: number;
};
type Comision = { id: string; estudioId: string; concepto: string; baseClp: number; meses: number; pct: number; montoClp: number; detalle: string | null; ts: string; pagadaEl: string | null };
type Informe = {
  aliado: Aliado;
  estudios: { id: string; nombre: string; subdominio: string; estado: string; aliadoDesde: string | null; contactoEmail: string | null; comisionClp: number }[];
  comisiones: Comision[];
  pendienteClp: number; pagadoClp: number; totalClp: number;
};

// ── Enlaces para invitar ─────────────────────────────────────────────────
//
// Los enlaces que abren el registro con el producto ya elegido, a mano para
// mandarlos por correo o WhatsApp a quien ya decidió probar. Con código de
// aliado, el estudio queda atribuido como si hubiera llegado por su enlace.
// La cortesía (con o sin tope) se pone después, desde Plan ›, cuando el
// estudio ya existe.
const ENLACES_REGISTRO: { etiqueta: string; detalle: string; ruta: string }[] = [
  { etiqueta: 'Chatbularis', detalle: 'Solo el asistente, prepago', ruta: '/?registro=chatbularis' },
  { etiqueta: 'Tabularis', detalle: 'Panel de gestión, con Chatbularis prepago', ruta: '/?registro=tabularis' },
];

const PanelInvitar: React.FC<{ onCerrar: () => void }> = ({ onCerrar }) => {
  const [ref, setRef] = useState('');
  const [copiado, setCopiado] = useState('');
  const codigo = ref.trim().toUpperCase().replace(/[^A-Z0-9-]/g, '');
  const url = (ruta: string) => `https://tabularis.cl${ruta}${codigo ? `&ref=${codigo}` : ''}`;
  const copiar = async (u: string) => {
    try { await navigator.clipboard.writeText(u); setCopiado(u); }
    catch { window.prompt('Copia el enlace:', u); }
  };

  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 100 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: '22px 24px', width: 'min(620px, 100%)', maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 19, margin: 0, color: C.navy }}>Enlaces para invitar</h3>
          <button onClick={onCerrar} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: C.muted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.55 }}>
          Abren el formulario para crear la cuenta con el producto ya elegido; ahí mismo se puede cambiar. Si va de cortesía, se la pones en su ficha (pestaña Chatbularis) cuando aparezca en Estudios.
        </div>

        <label style={{ ...lbl, marginTop: 14 }}>Código de aliado (opcional)</label>
        <input style={{ ...inp, textTransform: 'uppercase' }} value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Vacío: sin aliado" maxLength={16} />

        <div style={{ marginTop: 14 }}>
          {ENLACES_REGISTRO.map((e) => {
            const u = url(e.ruta);
            return (
              <div key={e.ruta} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${C.line}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{e.etiqueta} <span style={{ fontWeight: 400, color: C.muted, fontSize: 12.5 }}>· {e.detalle}</span></div>
                  <div style={{ fontSize: 12.5, color: C.blue2, overflowWrap: 'anywhere', fontFamily: 'ui-monospace, Menlo, monospace' }}>{u}</div>
                </div>
                <button onClick={() => copiar(u)} style={{ flexShrink: 0, padding: '7px 12px', borderRadius: 8, border: `1px solid ${copiado === u ? C.ok : C.line}`, background: '#fff', color: copiado === u ? C.ok : C.navy, cursor: 'pointer', fontSize: 13 }}>
                  {copiado === u ? 'Copiado ✓' : 'Copiar'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PanelAliados: React.FC<{ token: string }> = ({ token }) => {
  const [lista, setLista] = useState<Aliado[] | null>(null);
  const [abierto, setAbierto] = useState<Informe | null>(null);
  const [editando, setEditando] = useState<Partial<Aliado> | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [correo, setCorreo] = useState(false);

  const cargar = useCallback(async () => {
    try { setLista(await api<Aliado[]>('/plataforma/aliados', { token })); }
    catch { setErr('No se pudo cargar la lista de aliados.'); }
  }, [token]);
  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async (a: Aliado) => {
    setErr(''); setMsg('');
    try { setAbierto(await api<Informe>(`/plataforma/aliados/${a.id}/informe`, { token })); }
    catch { setErr('No se pudo cargar el informe.'); }
  };

  const guardar = async () => {
    if (!editando) return;
    setOcupado(true); setErr(''); setMsg('');
    const body = {
      codigo: editando.codigo, nombre: editando.nombre, contacto: editando.contacto || null,
      pctIa: Number(editando.pctIa ?? 10), mesesIa: editando.mesesIa || null,
      pctPanel: Number(editando.pctPanel ?? 0), mesesPanel: editando.mesesPanel || null,
      activo: editando.activo ?? true, notas: editando.notas || null,
    };
    try {
      if (editando.id) await api(`/plataforma/aliados/${editando.id}`, { method: 'PATCH', token, body });
      else {
        // Crear el aliado no le avisa nada: se abre su ficha con el correo
        // de instrucciones listo para revisar y mandar.
        const nuevo = await api<Aliado>('/plataforma/aliados', { method: 'POST', token, body });
        setEditando(null); await cargar();
        setAbierto(await api<Informe>(`/plataforma/aliados/${nuevo.id}/informe`, { token }));
        setMsg('Aliado creado. Mándale su enlace y su trato con el correo de instrucciones.');
        if (nuevo.contacto) setCorreo(true);
        return;
      }
      setEditando(null); setMsg('Guardado.');
      await cargar();
      if (abierto && editando.id === abierto.aliado.id) setAbierto(await api<Informe>(`/plataforma/aliados/${editando.id}/informe`, { token }));
    } catch (e: any) { setErr(e?.message || 'No se pudo guardar.'); }
    finally { setOcupado(false); }
  };

  const pagar = async () => {
    if (!abierto) return;
    if (!window.confirm(`¿Marcar como pagado ${clp(abierto.pendienteClp)} a ${abierto.aliado.nombre}? Hazlo después de transferir.`)) return;
    setOcupado(true); setErr(''); setMsg('');
    try {
      const r = await api<{ filas: number; montoClp: number }>(`/plataforma/aliados/${abierto.aliado.id}/pagar`, { method: 'POST', token, body: {} });
      setMsg(`${r.filas} ${r.filas === 1 ? 'comisión marcada' : 'comisiones marcadas'} como pagadas: ${clp(r.montoClp)}.`);
      setAbierto(await api<Informe>(`/plataforma/aliados/${abierto.aliado.id}/informe`, { token }));
      await cargar();
    } catch (e: any) { setErr(e?.message || 'No se pudo marcar el pago.'); }
    finally { setOcupado(false); }
  };

  const [ajuste, setAjuste] = useState({ estudioId: '', montoClp: '', detalle: '' });
  const corregir = async () => {
    if (!abierto) return;
    const monto = Math.round(Number(ajuste.montoClp.replace(/[^\d-]/g, '')) || 0);
    if (!monto || !ajuste.estudioId || ajuste.detalle.trim().length < 3) return;
    setOcupado(true); setErr(''); setMsg('');
    try {
      await api(`/plataforma/aliados/${abierto.aliado.id}/ajuste`, { method: 'POST', token, body: { estudioId: ajuste.estudioId, montoClp: monto, detalle: ajuste.detalle.trim() } });
      setAjuste({ estudioId: '', montoClp: '', detalle: '' }); setMsg('Corrección registrada.');
      setAbierto(await api<Informe>(`/plataforma/aliados/${abierto.aliado.id}/informe`, { token }));
      await cargar();
    } catch (e: any) { setErr(e?.message || 'No se pudo registrar la corrección.'); }
    finally { setOcupado(false); }
  };

  const enlace = (codigo: string) => `https://tabularis.cl/?ref=${codigo}`;
  const trato = (a: Aliado) =>
    [a.pctIa > 0 ? `Chatbularis ${a.pctIa}% de cada carga${a.mesesIa ? ` por ${a.mesesIa} meses` : ' mientras siga'}` : null,
     a.pctPanel > 0 ? `panel ${a.pctPanel}%${a.mesesPanel ? ` por ${a.mesesPanel} meses` : ' mientras siga'}` : null]
      .filter(Boolean).join(' · ') || 'sin comisión';
  const nombreEstudio = (id: string) => abierto?.estudios.find((e) => e.id === id)?.nombre ?? id;
  const campo = (k: keyof Aliado, v: any) => setEditando((e) => ({ ...(e || {}), [k]: v }));

  return (
    <div>
      {abierto && <button onClick={() => setAbierto(null)} style={{ background: 'none', border: 'none', color: C.blue2, cursor: 'pointer', fontSize: 13.5, padding: 0, marginBottom: 6 }}>‹ Todos los aliados</button>}
      <h1 style={titulo}>{abierto ? abierto.aliado.nombre : 'Aliados y comisiones'}</h1>
      <div style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '18px 20px', marginTop: 18, overflowX: 'auto' }}>

        {msg && <div style={{ background: '#E7F4E9', color: '#1B6B2B', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{msg}</div>}
        {err && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{err}</div>}

        {editando ? (
          <div style={{ marginTop: 14, border: `1px solid ${C.line}`, borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{editando.id ? 'Editar aliado' : 'Nuevo aliado'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
              <div><label style={lbl}>Código</label><input style={{ ...inp, textTransform: 'uppercase' }} value={editando.codigo ?? ''} onChange={(e) => campo('codigo', e.target.value.toUpperCase())} placeholder="JURIS" maxLength={16} /></div>
              <div><label style={lbl}>Nombre</label><input style={inp} value={editando.nombre ?? ''} onChange={(e) => campo('nombre', e.target.value)} placeholder="Empresa o persona" /></div>
            </div>
            <label style={{ ...lbl, marginTop: 10 }}>Correo de contacto</label>
            <input style={inp} value={editando.contacto ?? ''} onChange={(e) => campo('contacto', e.target.value)} placeholder="Para el informe mensual; también evita el auto-referido" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
              <div><label style={lbl}>Chatbularis % de cada carga</label><input style={inp} type="number" min={0} max={100} value={editando.pctIa ?? 10} onChange={(e) => campo('pctIa', e.target.value)} /></div>
              <div><label style={lbl}>Meses (vacío = siempre)</label><input style={inp} type="number" min={1} value={editando.mesesIa ?? ''} onChange={(e) => campo('mesesIa', e.target.value ? Number(e.target.value) : null)} /></div>
              <div><label style={lbl}>Panel %</label><input style={inp} type="number" min={0} max={100} value={editando.pctPanel ?? 0} onChange={(e) => campo('pctPanel', e.target.value)} /></div>
              <div><label style={lbl}>Meses (vacío = siempre)</label><input style={inp} type="number" min={1} value={editando.mesesPanel ?? ''} onChange={(e) => campo('mesesPanel', e.target.value ? Number(e.target.value) : null)} /></div>
            </div>
            <label style={{ ...lbl, marginTop: 10 }}>Notas</label>
            <input style={inp} value={editando.notas ?? ''} onChange={(e) => campo('notas', e.target.value)} placeholder="Plazo de la campaña, contacto, lo acordado" />
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginTop: 10 }}>
              <input type="checkbox" checked={editando.activo ?? true} onChange={(e) => campo('activo', e.target.checked)} /> Activo (su código sirve para registrar estudios nuevos)
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setEditando(null)} style={{ padding: '9px 14px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
              <button onClick={guardar} disabled={ocupado || !editando.codigo || !editando.nombre} style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{ocupado ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        ) : abierto ? (
          <>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'baseline', marginTop: 12, fontSize: 13, color: C.muted }}>
              <div>Código <strong style={{ color: C.navy }}>{abierto.aliado.codigo}</strong></div>
              <div>Trato: {trato(abierto.aliado)}</div>
              <div>Enlace: <span style={{ color: C.navy, fontFamily: 'monospace' }}>{enlace(abierto.aliado.codigo)}</span></div>
              <button onClick={() => setEditando(abierto.aliado)} style={{ background: 'none', border: 'none', color: C.blue2, cursor: 'pointer', fontSize: 13, padding: 0, textDecoration: 'underline' }}>Editar</button>
            </div>
            <div style={{ marginTop: 14, padding: '12px 16px', border: `1px solid ${C.line}`, borderRadius: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                <strong style={{ color: C.navy, display: 'block' }}>Correo con instrucciones</strong>
                {!abierto.aliado.contacto ? 'Sin correo de contacto: agrégalo en Editar para poder mandárselo.'
                  : abierto.aliado.instruccionesEnviadas ? `Enviado a ${abierto.aliado.contacto} el ${fmt(abierto.aliado.instruccionesEnviadas)}.`
                  : `Su enlace, su código y su trato, para ${abierto.aliado.contacto}. Todavía no se lo mandas.`}
              </div>
              <button onClick={() => setCorreo(true)} disabled={!abierto.aliado.contacto} style={botonSuave}>{abierto.aliado.instruccionesEnviadas ? 'Ver y reenviar…' : 'Ver y enviar…'}</button>
            </div>
            {correo && <DialogoCorreo titulo="Correo con instrucciones" ruta={`/plataforma/aliados/${abierto.aliado.id}/instrucciones`} quien="aliado"
              principal="tabularis" token={token} onCerrar={() => setCorreo(false)}
              onEnviado={async () => { await cargar(); setAbierto(await api<Informe>(`/plataforma/aliados/${abierto.aliado.id}/informe`, { token })); }} />}
            <div style={{ display: 'flex', gap: 14, marginTop: 14, flexWrap: 'wrap' }}>
              {[['Pendiente de pago', abierto.pendienteClp, abierto.pendienteClp > 0 ? '#8A2A2A' : C.navy], ['Ya pagado', abierto.pagadoClp, C.navy], ['Estudios que trajo', abierto.estudios.length, C.navy]].map(([k, v, color]) => (
                <div key={String(k)} style={{ border: `1px solid ${C.line}`, borderRadius: 10, padding: '10px 14px', minWidth: 150 }}>
                  <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: color as string, fontVariantNumeric: 'tabular-nums' }}>{typeof v === 'number' && String(k).includes('Estudios') ? v : clp(Number(v))}</div>
                </div>
              ))}
              {abierto.pendienteClp > 0 && (
                <button onClick={pagar} disabled={ocupado} style={{ alignSelf: 'center', padding: '10px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  Marcar {clp(abierto.pendienteClp)} como pagado
                </button>
              )}
            </div>

            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.muted, margin: '18px 0 6px' }}>Estudios que trajo</div>
            {abierto.estudios.length === 0 ? <div style={{ fontSize: 13, color: C.muted }}>Todavía ninguno.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ color: C.muted, fontSize: 11, textTransform: 'uppercase' }}><Th>Estudio</Th><Th>Desde</Th><Th>Estado</Th><Th c>Comisión acumulada</Th></tr></thead>
                <tbody>
                  {abierto.estudios.map((e) => (
                    <tr key={e.id} style={{ borderTop: `1px solid ${C.line}` }}>
                      <td style={td}><strong>{e.nombre}</strong><div style={{ fontSize: 11.5, color: C.muted }}>{e.subdominio}.tabularis.cl</div></td>
                      <td style={td}>{e.aliadoDesde ? fmt(e.aliadoDesde) : '—'}</td>
                      <td style={td}><Pastilla estado={e.estado} /></td>
                      <td style={{ ...td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{clp(e.comisionClp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: C.muted, margin: '18px 0 6px' }}>Comisiones</div>
            {abierto.comisiones.length === 0 ? <div style={{ fontSize: 13, color: C.muted }}>Todavía no se ha registrado ningún pago de sus estudios.</div> : abierto.comisiones.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', fontSize: 13, padding: '6px 0', borderTop: `1px solid ${C.line}` }}>
                <span style={{ minWidth: 0 }}>
                  {nombreEstudio(c.estudioId)} · {c.concepto === 'ia' ? 'Chatbularis' : c.concepto === 'panel' ? 'Panel' : 'Corrección'}
                  {c.concepto !== 'ajuste' && <span style={{ color: C.muted }}> · {c.pct}% de {clp(c.baseClp)}</span>}
                  <span style={{ color: C.muted, fontSize: 11.5 }}> · {fmt(c.ts)}</span>
                  {c.detalle && <div style={{ color: C.muted, fontSize: 11.5 }}>{c.detalle}</div>}
                </span>
                <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                  <span style={{ fontWeight: 600, color: c.montoClp < 0 ? '#8A2A2A' : C.navy }}>{c.montoClp < 0 ? '−' : ''}{clp(Math.abs(c.montoClp))}</span>
                  <div style={{ color: c.pagadaEl ? C.ok : '#8A2A2A', fontSize: 11.5 }}>{c.pagadaEl ? `pagada ${fmt(c.pagadaEl)}` : 'pendiente'}</div>
                </span>
              </div>
            ))}

            <div style={{ marginTop: 18, paddingTop: 12, borderTop: `1px solid ${C.line}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Corrección a mano</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 2fr auto', gap: 8, alignItems: 'end' }}>
                <div><label style={lbl}>Estudio</label>
                  <select style={inp} value={ajuste.estudioId} onChange={(e) => setAjuste({ ...ajuste, estudioId: e.target.value })}>
                    <option value="">—</option>
                    {abierto.estudios.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                  </select></div>
                <div><label style={lbl}>Monto (± pesos)</label><input style={inp} inputMode="numeric" value={ajuste.montoClp} onChange={(e) => setAjuste({ ...ajuste, montoClp: e.target.value })} placeholder="-7500" /></div>
                <div><label style={lbl}>Motivo</label><input style={inp} value={ajuste.detalle} onChange={(e) => setAjuste({ ...ajuste, detalle: e.target.value })} placeholder="Devolución: pago registrado dos veces" /></div>
                <button onClick={corregir} disabled={ocupado || !ajuste.estudioId || !ajuste.montoClp || ajuste.detalle.trim().length < 3} style={{ padding: '11px 14px', borderRadius: 9, border: `1px solid ${C.line}`, background: '#fff', color: C.navy, fontSize: 13, cursor: 'pointer' }}>Registrar</button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p style={{ color: C.muted, fontSize: 13, margin: '10px 0 0', lineHeight: 1.6 }}>
              Cada aliado tiene un código y un enlace propio. El estudio que se registra con él queda atribuido de por vida,
              y cada vez que registras un pago de ese estudio se devenga la comisión según el trato del aliado. La prueba gratis no genera comisión.
            </p>
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setEditando({ pctIa: 10, mesesIa: 12, pctPanel: 0, activo: true })} style={{ padding: '9px 14px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Nuevo aliado</button>
            </div>
            {!lista ? <div style={{ color: C.muted, fontSize: 14, marginTop: 16 }}>Cargando…</div> : lista.length === 0 ? (
              <div style={{ color: C.muted, fontSize: 14, marginTop: 16 }}>Todavía no hay aliados.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 14 }}>
                <thead><tr style={{ color: C.muted, fontSize: 11, textTransform: 'uppercase' }}><Th>Aliado</Th><Th>Trato</Th><Th c>Estudios</Th><Th c>Pendiente</Th><Th>{''}</Th></tr></thead>
                <tbody>
                  {lista.map((a) => (
                    <tr key={a.id} style={{ borderTop: `1px solid ${C.line}`, opacity: a.activo ? 1 : 0.55 }}>
                      <td style={td}><strong>{a.nombre}</strong><div style={{ fontSize: 11.5, color: C.muted }}>{a.codigo}{!a.activo && ' · inactivo'} · {enlace(a.codigo)}{a.activo && !a.instruccionesEnviadas && <span style={{ color: '#8A2A2A' }}> · sin instrucciones</span>}</div></td>
                      <td style={{ ...td, color: C.muted }}>{trato(a)}</td>
                      <td style={{ ...td, textAlign: 'center' }}>{a.estudios}</td>
                      <td style={{ ...td, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: a.pendienteClp > 0 ? '#8A2A2A' : C.navy, fontWeight: a.pendienteClp > 0 ? 700 : 400 }}>{clp(a.pendienteClp)}</td>
                      <td style={td}><button onClick={() => abrir(a)} style={{ fontSize: 12, padding: '5px 10px', borderRadius: 7, cursor: 'pointer', border: `1px solid ${C.line}`, background: '#fff', color: C.blue2, whiteSpace: 'nowrap' }}>Informe ›</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const lbl: React.CSSProperties = { display: 'block', fontSize: 13, color: '#6B82A0', marginBottom: 5 };
const inp: React.CSSProperties = { width: '100%', padding: '11px 12px', border: '1px solid #DDE6F3', borderRadius: 9, fontSize: 15, fontFamily: 'Inter, sans-serif', color: '#0D1B35', boxSizing: 'border-box' };

export default PlataformaConsola;

/* ── Base jurídica de Chatbularis IA ────────────────────────────────
   Vive en la consola dueño y no en el panel de los estudios: el corpus es un
   activo de Tabularis, compartido por todos, con jurisprudencia general y
   transversal. Ningún estudio —ni su admin— puede escribir acá, porque
   estaría cambiando lo que ven todos los demás.

   Se suben muchos archivos de una vez (arrastrándolos o eligiéndolos): van
   uno por uno, y si uno falla los demás siguen y al final se dice cuáles no
   entraron. La indexación corre en segundo plano en el servidor.

   Un .zip se abre en el navegador con lo mismo que usa Importar del panel:
   cada documento de adentro se sube por separado, con su propia revisión de
   repetidos, y la basura del sistema (__MACOSX, .DS_Store) se salta. */
const ACEPTA_CORPUS = ['.pdf', '.doc', '.docx', '.txt'];

/** Una tanda de cientos de archivos alcanza el techo general del servidor (300
 *  peticiones por minuto por IP). Ante eso se espera un minuto y se reintenta
 *  el mismo archivo; repetirlo no duplica nada porque la huella reaprovecha la
 *  fila que haya quedado a medias. */
const ESPERA_LIMITE_MS = 60_000;
const REINTENTOS_LIMITE = 3;
/** Con tres subidas a S3 fallidas seguidas se corta la tanda: es un problema
 *  de conexión o de configuración y los archivos que faltan fallarían igual. */
const CORTE_S3 = 3;
const esperar = (ms: number) => new Promise((ok) => setTimeout(ok, ms));

const BaseJuridica: React.FC<{ token: string }> = ({ token }) => {
  const [docs, setDocs] = useState<IA.DocumentoCorpus[] | null>(null);
  const [totales, setTotales] = useState<Pick<IA.ListaCorpus, 'totalDocumentos' | 'porEstado' | 'totalFragmentos'> | null>(null);
  const [habilitado, setHabilitado] = useState(true);
  const [subida, setSubida] = useState<{ hecho: number; total: number; actual: string; enPausa?: boolean } | null>(null);
  const [fallidos, setFallidos] = useState<{ nombre: string; motivo: string }[]>([]);
  const [error, setError] = useState('');
  const [encima, setEncima] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [verEstado, setVerEstado] = useState<IA.FiltroCorpus>('todos');
  // La búsqueda va al servidor: se espera a que se deje de escribir.
  const [buscarYa, setBuscarYa] = useState('');
  useEffect(() => { const t = setTimeout(() => setBuscarYa(buscar), 300); return () => clearTimeout(t); }, [buscar]);
  const fileRef = useRef<HTMLInputElement>(null);

  const recargar = useCallback(async () => {
    try {
      const r = await IA.listarCorpus(token, { q: buscarYa, estado: verEstado });
      setDocs(r.documentos);
      setTotales({ totalDocumentos: r.totalDocumentos, porEstado: r.porEstado, totalFragmentos: r.totalFragmentos });
      setHabilitado(r.habilitado);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo cargar la base jurídica.');
    }
  }, [token, buscarYa, verEstado]);

  useEffect(() => { void recargar(); }, [recargar]);

  // Mientras haya documentos indexándose se refresca solo: la indexación corre
  // en segundo plano y puede tardar minutos según el tamaño del archivo.
  useEffect(() => {
    if (!totales || totales.porEstado.procesando + totales.porEstado.pendiente === 0) return;
    const t = setTimeout(() => { void recargar(); }, 4000);
    return () => clearTimeout(t);
  }, [totales, recargar]);

  const subir = async (leer: () => Promise<ArchivoConRuta[]>) => {
    let lista: ArchivoConRuta[];
    setError(''); setFallidos([]);
    try { lista = (await leer()).filter((x) => !esBasura(x.ruta, x.archivo.size)); }
    catch (e: any) { setError(e?.message ?? 'No se pudo abrir el .zip.'); return; }
    const admitido = (x: ArchivoConRuta) => ACEPTA_CORPUS.some((ext) => x.archivo.name.toLowerCase().endsWith(ext));
    const validos = lista.filter(admitido).map((x) => x.archivo);
    const rechazados = lista.filter((x) => !admitido(x)).map((x) => ({ nombre: x.ruta, motivo: 'formato no admitido (PDF, Word o texto)' }));
    setFallidos(rechazados);
    if (!validos.length) return;
    const malos: { nombre: string; motivo: string }[] = [...rechazados];
    let fallasS3 = 0;
    let i = 0;
    for (; i < validos.length && fallasS3 < CORTE_S3; i++) {
      const actual = validos[i].name;
      setSubida({ hecho: i, total: validos.length, actual });
      for (let intento = 0; ; intento++) {
        try { await IA.subirAlCorpus(token, validos[i]); fallasS3 = 0; break; }
        catch (e: any) {
          if (IA.esLimiteDePeticiones(e) && intento < REINTENTOS_LIMITE) {
            setSubida({ hecho: i, total: validos.length, actual, enPausa: true });
            await esperar(ESPERA_LIMITE_MS);
            setSubida({ hecho: i, total: validos.length, actual });
            continue;
          }
          if (e instanceof IA.ErrorSubidaS3) fallasS3++;
          malos.push({ nombre: actual, motivo: e?.message ?? 'no se pudo subir' });
          break;
        }
      }
      // Se refresca cada tanto para que la lista muestre lo que ya entró.
      if (i % 5 === 4) void recargar();
    }
    if (fallasS3 >= CORTE_S3 && i < validos.length) {
      setError(`Se detuvo la carga: ${CORTE_S3} archivos seguidos no se pudieron subir al almacenamiento. `
        + `Los ${validos.length - i} que seguían no se intentaron. Revisa la conexión; si sigue, el navegador no tiene permiso para el bucket del corpus (CORS del bucket o connect-src de la CSP, ver scripts/cloudfront-corpus.sh).`);
    }
    setSubida(null);
    setFallidos(malos);
    await recargar();
  };

  const eliminar = async (d: IA.DocumentoCorpus) => {
    if (!window.confirm(`¿Eliminar "${d.nombre}" de la base jurídica? Deja de usarse en las respuestas de todos los estudios.`)) return;
    try { await IA.eliminarDelCorpus(token, d.id); await recargar(); }
    catch (e: any) { setError(e?.message ?? 'No se pudo eliminar.'); }
  };

  const conError = totales?.porEstado.error ?? 0;
  const enCurso = (totales?.porEstado.pendiente ?? 0) + (totales?.porEstado.procesando ?? 0);
  const visibles = docs ?? [];
  const ocupado = !!subida;

  const estadoDoc = (d: IA.DocumentoCorpus): [string, string] =>
    d.estado === 'listo' ? [`Indexado · ${d.fragmentos.toLocaleString('es-CL')} fragmentos`, C.ok]
      : d.estado === 'error' ? ['Error al indexar', '#A32A2A']
        : d.estado === 'procesando' ? ['Indexando…', C.blue2] : ['En cola', C.muted];
  const filtroBtn = (id: typeof verEstado, texto: string, n?: number) => (
    <button onClick={() => setVerEstado(id)} aria-pressed={verEstado === id}
      style={{ border: `1px solid ${verEstado === id ? C.navy : C.line}`, background: verEstado === id ? C.navy : '#fff', color: verEstado === id ? '#fff' : C.navy, borderRadius: 999, padding: '5px 11px', fontSize: 12.5, cursor: 'pointer' }}>
      {texto}{n != null && ` · ${n}`}
    </button>
  );

  return (
    <section style={{ ...caja, marginTop: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 19, margin: 0 }}>Base jurídica</h2>
        <span style={{ fontSize: 12.5, color: C.muted, fontVariantNumeric: 'tabular-nums' }}>
          {totales ? `${totales.totalDocumentos.toLocaleString('es-CL')} documentos · ${totales.totalFragmentos.toLocaleString('es-CL')} fragmentos` : 'Cargando…'}
        </span>
      </div>
      <p style={{ fontSize: 13, color: C.muted, margin: '6px 0 0', lineHeight: 1.55 }}>
        Jurisprudencia, doctrina y modelos que Chatbularis consulta en cada respuesta. La comparten todos los estudios.
        Las leyes no van acá: se leen directo de la BCN.
      </p>

      {!habilitado && (
        <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>
          Falta configurar CORPUS_BUCKET en el servidor: no se pueden subir documentos todavía.
        </div>
      )}

      {/* Zona de subida: se arrastra una tanda de archivos o se eligen. */}
      <div
        onDragOver={(ev) => { ev.preventDefault(); if (habilitado && !ocupado) setEncima(true); }}
        onDragLeave={() => setEncima(false)}
        onDrop={(ev) => { ev.preventDefault(); setEncima(false); if (habilitado && !ocupado) { const items = ev.dataTransfer.items; void subir(() => leerArrastre(items)); } }}
        style={{
          marginTop: 14, border: `2px dashed ${encima ? C.blue2 : C.line}`, background: encima ? '#EEF4FD' : '#FAFCFF',
          borderRadius: 12, padding: '22px 16px', textAlign: 'center', transition: 'background .15s, border-color .15s',
        }}>
        {subida ? (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {subida.enPausa ? `En pausa un minuto: el servidor pidió ir más lento (${subida.hecho + 1} de ${subida.total})` : `Subiendo ${subida.hecho + 1} de ${subida.total}`}
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, overflowWrap: 'anywhere' }}>{subida.actual}</div>
            <div style={{ height: 6, background: '#EEF2F8', borderRadius: 99, overflow: 'hidden', maxWidth: 360, margin: '10px auto 0' }}>
              <div style={{ width: `${(subida.hecho / subida.total) * 100}%`, height: '100%', background: C.blue2, transition: 'width .3s' }} />
            </div>
          </div>
        ) : (<>
          <div style={{ fontSize: 14, color: C.navy }}>Arrastra aquí los archivos, todos los que quieras</div>
          <div style={{ fontSize: 12.5, color: C.muted, margin: '4px 0 12px' }}>PDF, Word o texto, sueltos o en un .zip. Se indexan solos en segundo plano.</div>
          <button onClick={() => fileRef.current?.click()} disabled={!habilitado}
            style={{ padding: '9px 16px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontSize: 14, fontWeight: 600, cursor: habilitado ? 'pointer' : 'default', opacity: habilitado ? 1 : 0.5 }}>
            Elegir archivos
          </button>
        </>)}
        <input ref={fileRef} type="file" multiple accept={[...ACEPTA_CORPUS, '.zip'].join(',')} style={{ display: 'none' }}
          onChange={(ev) => {
            // Se copia antes de vaciar el input: FileList es una vista viva y
            // queda vacía al poner value = ''.
            const f = Array.from(ev.target.files ?? []); ev.target.value = '';
            void subir(() => leerSeleccion(f));
          }} />
      </div>

      {error && <div style={{ background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 12 }}>{error}</div>}
      {fallidos.length > 0 && (
        <div style={{ background: '#FFF6E5', border: '1px solid #E8C98A', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 12, lineHeight: 1.55 }}>
          <strong>{fallidos.length} {fallidos.length === 1 ? 'archivo no entró' : 'archivos no entraron'}:</strong>
          {fallidos.map((f) => <div key={f.nombre} style={{ overflowWrap: 'anywhere' }}>{f.nombre} · <span style={{ color: C.muted }}>{f.motivo}</span></div>)}
        </div>
      )}

      {totales && totales.totalDocumentos > 0 && (<>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 18 }}>
          {filtroBtn('todos', 'Todos')}
          {(enCurso > 0 || verEstado === 'enCurso') && filtroBtn('enCurso', 'Indexando', enCurso)}
          {(conError > 0 || verEstado === 'error') && filtroBtn('error', 'Con error', conError)}
          <input value={buscar} onChange={(ev) => setBuscar(ev.target.value)} placeholder="Buscar por nombre…"
            style={{ marginLeft: 'auto', padding: '7px 11px', border: `1px solid ${C.line}`, borderRadius: 8, fontSize: 13.5, width: 'min(220px, 100%)' }} />
        </div>
        <div style={{ marginTop: 8, maxHeight: 520, overflowY: 'auto' }}>
          {visibles.map((d) => {
            const [txt, color] = estadoDoc(d);
            return (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderTop: `1px solid ${C.line}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.nombre}>{d.nombre}</div>
                  <div style={{ fontSize: 12, color }}>{txt}<span style={{ color: C.muted }}> · {fmt(d.creado)}</span></div>
                  {d.error && <div style={{ fontSize: 12, color: '#A32A2A' }}>{d.error}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => IA.reindexarCorpus(token, d.id).then(recargar).catch((e: any) => setError(e?.message ?? 'No se pudo reindexar.'))} style={botonSuave}>Reindexar</button>
                  <button onClick={() => eliminar(d)} style={{ ...botonSuave, color: '#A32A2A', borderColor: '#E8C4C4' }}>Eliminar</button>
                </div>
              </div>
            );
          })}
          {visibles.length === 0 && <div style={{ fontSize: 13, color: C.muted, padding: '14px 0' }}>Ningún documento coincide.</div>}
        </div>
        {visibles.length >= IA.LIMITE_LISTA_CORPUS && (
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 8 }}>
            Se muestran los {IA.LIMITE_LISTA_CORPUS.toLocaleString('es-CL')} más recientes. Para encontrar otro, búscalo por nombre.
          </div>
        )}
      </>)}
      {totales && totales.totalDocumentos === 0 && (
        <p style={{ fontSize: 13, color: C.muted, marginTop: 14 }}>La base jurídica está vacía. El asistente funciona igual, apoyado en las leyes de la BCN.</p>
      )}
    </section>
  );
};
